import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DayPicker, type DateRange } from 'react-day-picker'
import { fr as dpFr } from 'react-day-picker/locale'
import 'react-day-picker/style.css'
import i18n from '@/i18n'
import { supabase } from '@/lib/supabase'
import { notifyEvent } from '@/lib/notify'
import type {
  CardComment,
  List,
  Message,
  Priority,
  Project,
  ProposedSplit,
  ReminderFreq,
  ReminderMonthlyMode,
  ReplyTone,
  Ticket,
  UserProfile,
} from '@/lib/types'
import { Avatar } from './Avatar'
import { RichEditor } from './RichEditor'
import { htmlToText, isRichEmpty, sanitizeRichHtml } from '@/lib/richtext'
import { InlineAudio, type AudioSpeed } from './InlineAudio'
import { ToneSlider } from './ToneSlider'
import { WaveformPlayer } from './WaveformPlayer'
import { DocxPreview } from './DocxPreview'
import { useConfirm, useNotify } from './ConfirmDialog'
import {
  cn,
  displayListName,
  formatAiDate,
  formatDateTime,
  formatRelative,
  formatScheduled,
  googleCalendarUrl,
  groupMedia,
  isDocx,
  isTicketUrgent,
  labelColor,
  listDotClass,
  mediaKind,
  nextReminderAfter,
  type ReminderRule,
  PRIORITY_META,
  PRIORITY_ORDER,
  priorityLabel,
  profileName,
} from '@/lib/utils'
import {
  X,
  Trash2,
  AlignLeft,
  Paperclip,
  Tag,
  FileText,
  Loader2,
  Archive,
  Captions,
  Sparkles,
  Scissors,
  Check,
  UserPlus,
  Play,
  ArrowRightLeft,
  HelpCircle,
  CalendarDays,
  Flame,
  Copy,
  MessagesSquare,
  MessageCircleMore,
  Send,
  Pencil,
  Lock,
  Clock,
  SlidersHorizontal,
  ChevronDown,
  Star,
  Bell,
  Mic,
  Square,
  SpellCheck,
  Smile,
} from 'lucide-react'

type AttachmentKind = 'image' | 'video' | 'audio' | 'document'

/** Quick-reaction emojis available on every message. Shared across the team
 *  (toggle_message_tag RPC). The `key` is stored in messages.tags; the
 *  `emoji` is the displayed glyph; `tipKey` translates the button tooltip. */
const MESSAGE_REACTIONS: { key: string; emoji: string; tipKey: string }[] = [
  { key: 'check', emoji: '✓', tipKey: 'detail.reactions.check' },
  { key: 'validate', emoji: '✅', tipKey: 'detail.reactions.validate' },
  { key: 'cancel', emoji: '❌', tipKey: 'detail.reactions.cancel' },
  { key: 'warning', emoji: '⚠️', tipKey: 'detail.reactions.warning' },
  { key: 'smile', emoji: '😊', tipKey: 'detail.reactions.smile' },
  { key: 'love', emoji: '❤️', tipKey: 'detail.reactions.love' },
  { key: 'laugh', emoji: '😂', tipKey: 'detail.reactions.laugh' },
]

/** A file staged in the reply composer (picked or recorded), uploaded to
 *  the `media` bucket then sent to WhatsApp as its own message. */
interface PendingAttachment {
  id: string
  url: string | null
  kind: AttachmentKind
  filename: string
  mime: string
  uploading: boolean
  error?: boolean
  /** Recorded (vs picked) — drives the « transcrire » affordance. */
  recorded?: boolean
  /** For audio: transcribe with Whisper after sending (optional). */
  transcribe?: boolean
}

/** Map a MIME type (and extension fallback) to the WhatsApp media kind. */
function mimeToKind(mime: string, ext: string): AttachmentKind {
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  const e = ext.toLowerCase()
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(e)) return 'image'
  if (['mp4', 'mov', 'webm', 'mkv'].includes(e)) return 'video'
  if (['ogg', 'oga', 'opus', 'mp3', 'm4a', 'aac', 'wav', 'amr'].includes(e))
    return 'audio'
  return 'document'
}

/** Pick the best recording MIME the browser supports, preferring formats
 *  WhatsApp can ingest (OGG/OPUS, MP4/M4A) over Chrome's default webm. */
function pickRecordingMime(): string {
  const candidates = [
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/mpeg',
    'audio/webm;codecs=opus',
    'audio/webm',
  ]
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder })
    .MediaRecorder
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const c of candidates) if (MR.isTypeSupported(c)) return c
  }
  return ''
}

function extForMime(mime: string): string {
  const m = mime.toLowerCase()
  if (m.includes('ogg')) return 'ogg'
  if (m.includes('mp4')) return 'm4a'
  if (m.includes('mpeg')) return 'mp3'
  if (m.includes('webm')) return 'webm'
  return 'bin'
}

interface Props {
  ticket: Ticket | null
  lists: List[]
  profilesById: Record<string, UserProfile>
  /** When true, audio source messages without a summary are transcribed
   *  automatically as soon as the card opens (per-user preference). */
  autoTranscribe: boolean
  /** Whether THIS card's audio is being analysed (card-level flag from App).
   *  Drives the prominent "analyse en cours" banner so the feedback matches
   *  the board chip. */
  cardTranscribing?: boolean
  /** Toggle the card-level transcribing flag (so the modal's own transcription
   *  shows the board chip + banner consistently). */
  onTranscribingChange?: (ticketId: string, on: boolean) => void
  /** AI signal-detection toggles (per-user prefs). When a transcription
   *  surfaces a date / question / urgency, we apply it to the card if the
   *  matching toggle is on. */
  aiDetect: { date: boolean; question: boolean; urgent: boolean; autoPriority: boolean }
  onClose: () => void
  onUpdate: (id: string, patch: Partial<Ticket>) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  onArchive: (id: string) => void
  /** Trigger Claude analysis on the whole card — returns the proposed splits. */
  onAnalyzeSplit: (ticketId: string) => Promise<ProposedSplit[]>
  /** Apply a Claude-proposed split: archive the parent, create N new cards. */
  onApplySplit: (ticket: Ticket, splits: ProposedSplit[]) => Promise<void>
  /** Dismiss the proposal at the ticket level (clears tickets.proposed_splits). */
  onDismissSplit: (ticketId: string) => Promise<void>
  /** Manual split: extract the selected conversation messages into a brand
   *  new card (kept in the original too — the messages live in both). */
  onSplitMessagesToCard: (ticket: Ticket, messageIds: string[]) => Promise<void>
  /** All user_ids who can be subscribed (owner + project members). */
  subscribableUserIds: string[]
  /** Current subscribers of the open card — null = unknown. */
  subscriberIds: Set<string> | null
  /** Used to flag "you" in the picker. */
  currentUserId: string
  /** Subscribe / unsubscribe a user (optimistic + email notify upstream). */
  onToggleSubscriber: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
  /** Opens the project-members modal — the only place to *invite* a new
   *  collaborator (the card-level picker only toggles people already on the
   *  project). Wired here so the user can jump to it without closing the card. */
  onOpenMembers: () => void
  /** Global audio playback rate (1×, 1.25×, …, 2×) and its setter, shared
   *  with the InlineAudio players on the board. */
  audioSpeed?: AudioSpeed
  onAudioSpeedChange?: (rate: AudioSpeed) => void
  /** Open the cross-project « Déplacer la carte » dialog from the
   *  detail modal header. The parent closes this modal first (we no
   *  longer need it after a successful move) and opens the move dialog
   *  on top. */
  onMoveCard?: (ticket: Ticket) => void
  /** Project that owns the card — drives the reply permission, the 24h
   *  window and the recipient. Null while loading / not found. */
  project?: Project | null
  /** Patch a few project fields live from the card (suggestion tone +
   *  on/off toggle), persisted optimistically. Owner-gated upstream. */
  onPatchProject?: (id: string, patch: Partial<Project>) => void | Promise<void>
  /** Deduplicated labels used anywhere in this project — surfaced as
   *  one-click suggestion chips in the labels popover so the user can
   *  reapply an existing label without retyping it. */
  projectLabels?: string[]
}

export function TicketDetailModal({
  ticket,
  lists,
  profilesById,
  autoTranscribe,
  cardTranscribing = false,
  onTranscribingChange,
  aiDetect,
  onClose,
  onUpdate,
  onDelete,
  onArchive,
  onAnalyzeSplit,
  onApplySplit,
  onDismissSplit,
  onSplitMessagesToCard,
  subscribableUserIds,
  subscriberIds,
  currentUserId,
  onToggleSubscriber,
  onOpenMembers,
  audioSpeed,
  onAudioSpeedChange,
  onMoveCard,
  project,
  onPatchProject,
  projectLabels,
}: Props) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [zoom, setZoom] = useState<string | null>(null)
  const [transcribing, setTranscribing] = useState<string | null>(null)
  // .docx attachment currently open in the lightweight preview modal.
  const [docxPreviewUrl, setDocxPreviewUrl] = useState<string | null>(null)
  // Which media transcriptions (keyed by media URL) are expanded. Empty by
  // default → every transcription is a collapsed spoiler so the Contenu tab
  // leads with the description and the transcription stays out of the way.
  const [expandedTx, setExpandedTx] = useState<Set<string>>(new Set())
  function toggleTx(url: string) {
    setExpandedTx((prev) => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  }
  // Ticket-level split state (replaces the per-message flow).
  const [analyzingTicket, setAnalyzingTicket] = useState(false)
  const [applyingSplit, setApplyingSplit] = useState(false)
  // Manual message-level split (Messagerie tab): pick messages → new card.
  const [selectMode, setSelectMode] = useState(false)
  const [selectedMsgIds, setSelectedMsgIds] = useState<Set<string>>(new Set())
  const [splittingMessages, setSplittingMessages] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingDesc, setEditingDesc] = useState(false)
  // The "Messages WhatsApp source" section is collapsed by default —
  // it's the raw, noisy WhatsApp input. The user usually wants the AI-
  // distilled card content (title / description / Médias) front and
  // centre, and only digs into the raw messages occasionally.
  const [titleDraft, setTitleDraft] = useState('')
  const [descDraft, setDescDraft] = useState('')
  const [descCopied, setDescCopied] = useState(false)
  const [labelDraft, setLabelDraft] = useState('')
  // User comments — internal team discussion on the card, scoped to
  // project members via RLS. Never visible to the WhatsApp client.
  const [comments, setComments] = useState<CardComment[]>([])
  const [commentDraft, setCommentDraft] = useState('')
  const [postingComment, setPostingComment] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentDraft, setEditingCommentDraft] = useState('')
  const [savingCommentEdit, setSavingCommentEdit] = useState(false)
  // Attachments staged for the next posted comment (drag/drop or paperclip).
  // Same shape as the reply composer's pendingAttachments, minus the
  // recorded / transcribe fields (comments don't record audio).
  const [commentAttachments, setCommentAttachments] = useState<
    {
      id: string
      url: string | null
      kind: AttachmentKind
      filename: string
      mime: string
      uploading: boolean
      error?: boolean
    }[]
  >([])
  const [isDraggingCommentFiles, setIsDraggingCommentFiles] = useState(false)
  const commentDragDepthRef = useRef(0)
  const commentFileInputRef = useRef<HTMLInputElement | null>(null)
  // Card-level media uploads (Contenu tab drop zone) — go directly into
  // ticket.media_urls so they land in the hero media section. We only
  // track in-progress / errored uploads here; successful ones disappear
  // from this list as soon as they're appended to ticket.media_urls.
  const [cardUploads, setCardUploads] = useState<
    { id: string; filename: string; uploading: boolean; error?: boolean }[]
  >([])
  const [isDraggingCardFiles, setIsDraggingCardFiles] = useState(false)
  const cardDragDepthRef = useRef(0)
  const cardFileInputRef = useRef<HTMLInputElement | null>(null)
  // When the user drops a file into the comment composer and adds no
  // caption, we auto-publish a comment with the attachment after a short
  // grace period — so leaving the card without clicking « Publier » still
  // persists the drop (the most common user expectation here).
  const [autoPostPending, setAutoPostPending] = useState(false)
  const autoPostTimerRef = useRef<number | null>(null)
  // ── Main column tabs (Contenu / Messagerie / Commentaires) ─────────
  const [activeTab, setActiveTab] = useState<
    'contenu' | 'messagerie' | 'commentaires'
  >('contenu')
  // Header workflow controls — which popover (status / priority / members /
  // labels) is currently open. The right rail was removed in favour of these
  // compact top-banner popovers (more board real-estate for the content).
  const [openCtl, setOpenCtl] = useState<
    null | 'status' | 'priority' | 'members' | 'labels' | 'date' | 'reminder'
  >(null)
  // ── WhatsApp reply ─────────────────────────────────────────────────
  // The composer at the bottom of the Messagerie tab always replies to
  // the conversation (quoting the latest inbound message), so there's no
  // per-message target to track.
  const [replyDraft, setReplyDraft] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [correcting, setCorrecting] = useState(false)
  // Emoji picker (composer) — opens a small popover with the same set of
  // reactions as the per-message bar; clicking inserts at the cursor.
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  // True while the user is dragging files over the composer drop zone.
  // Driven by a small dragenter/leave counter so the highlight doesn't
  // flicker when the cursor crosses child elements.
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const dragDepthRef = useRef(0)
  const [replySuggestions, setReplySuggestions] = useState<string[]>([])
  const [replySuggLoading, setReplySuggLoading] = useState(false)
  // Inline « réglages des suggestions » popover (tone + on/off) opened
  // from the messagerie composer, so the user can retune on the fly.
  const [tonePopoverOpen, setTonePopoverOpen] = useState(false)
  const [lastInboundAt, setLastInboundAt] = useState<number | null>(null)
  // Attachments staged in the composer (files picked or audio recorded),
  // each uploaded to the `media` bucket then sent as its own WA message.
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([])
  // Live audio recording state.
  const [recording, setRecording] = useState(false)
  const [recordSecs, setRecordSecs] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordChunksRef = useRef<Blob[]>([])
  const recordTimerRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const replyComposerRef = useRef<HTMLTextAreaElement | null>(null)
  const titleRef = useRef<HTMLTextAreaElement | null>(null)
  const descRef = useRef<HTMLDivElement | null>(null)
  const confirm = useConfirm()
  const notify = useNotify()

  // Re-fetch the source messages ONLY when the opened card actually
  // changes — i.e. its id, or the SET of source message ids it points
  // to. Earlier the dep was the whole `ticket` object, so every
  // optimistic update from the user (clicking priority / list /
  // labels) gave us a new object reference and the modal re-fetched
  // its messages, briefly flashing its loading state. The visible
  // "blink" was the modal remounting its inner content.
  //
  // `ticket.id` covers "different card opened". The stringified ids
  // join covers "same card got new source messages" (rare, but happens
  // via realtime when a vocal is auto-added to the card by ingestion).
  const ticketId = ticket?.id ?? null
  const sourceIds = ticket?.source_message_ids?.join(',') ?? ''
  useEffect(() => {
    if (!ticketId || sourceIds === '') {
      setMessages([])
      return
    }
    let cancelled = false
    setLoading(true)
    supabase
      .from('messages')
      .select('*')
      .in('id', sourceIds.split(','))
      .order('received_at', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) console.error(error)
        setMessages(data ?? [])
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ticketId, sourceIds])

  // Reset the title / description drafts ONLY when the opened card
  // changes. Same pattern: pinning to ticket.id avoids stomping the
  // user's in-flight edit every time another field of the same card
  // is patched via the contextual menu.
  useEffect(() => {
    if (!ticket) return
    setTitleDraft(ticket.title)
    setDescDraft(ticket.description ?? '')
    // Clear any in-flight composer / edit draft when switching cards.
    setCommentDraft('')
    setEditingCommentId(null)
    setEditingCommentDraft('')
    setActiveTab('contenu')
    setReplyDraft('')
    setReplySuggestions([])
    setPendingAttachments([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId])

  // Stop any in-flight recording + clear its timer when the modal closes
  // or the card changes (avoids a dangling MediaRecorder / interval).
  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
      if (recordTimerRef.current) window.clearInterval(recordTimerRef.current)
    }
  }, [ticketId])

  // Most recent INBOUND message timestamp for the project — drives the
  // 24h WhatsApp reply window. Project-wide (the window is between the
  // WABA and the contact, regardless of which card the message is on).
  const projectId = project?.id ?? null
  useEffect(() => {
    if (!projectId) {
      setLastInboundAt(null)
      return
    }
    let cancelled = false
    supabase
      .from('messages')
      .select('received_at')
      .eq('project_id', projectId)
      .eq('direction', 'inbound')
      .order('received_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        setLastInboundAt(
          data?.received_at ? new Date(data.received_at).getTime() : null,
        )
      })
    return () => {
      cancelled = true
    }
  }, [projectId, messages])

  // Load + subscribe to user comments for the open card. Realtime
  // keeps every project member's modal in sync — new comments, edits,
  // soft-deletes all flow through `card_comments` postgres_changes.
  useEffect(() => {
    if (!ticketId) {
      setComments([])
      return
    }
    let cancelled = false
    supabase
      .from('card_comments')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('load card comments', error)
          return
        }
        setComments((data as CardComment[]) ?? [])
      })

    const channel = supabase
      .channel(`card_comments:${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'card_comments',
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const next = payload.new as CardComment
            setComments((arr) =>
              arr.some((c) => c.id === next.id)
                ? arr
                : [...arr, next].sort((a, b) =>
                    a.created_at.localeCompare(b.created_at),
                  ),
            )
          } else if (payload.eventType === 'UPDATE') {
            const next = payload.new as CardComment
            setComments((arr) => arr.map((c) => (c.id === next.id ? next : c)))
          } else if (payload.eventType === 'DELETE') {
            const old = payload.old as { id: string }
            setComments((arr) => arr.filter((c) => c.id !== old.id))
          }
        },
      )
      .subscribe()

    return () => {
      cancelled = true
      supabase.removeChannel(channel)
    }
  }, [ticketId])

  // Auto-transcription: pick the next audio source message without a summary
  // and fire transcribeMessage on it. The effect re-runs when messages
  // update (a transcription just finished), so the queue drains serially.
  useEffect(() => {
    // Bail if already analysing here (transcribing) OR at the App level
    // (cardTranscribing — e.g. the auto-on-arrival path) to avoid a double run.
    if (!autoTranscribe || loading || transcribing || cardTranscribing) return
    const next = messages.find(
      (m) => !m.summary && groupMedia(m.media_urls).audio.length > 0,
    )
    if (next) transcribeMessage(next)
    // transcribeMessage is recreated each render but only reads stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTranscribe, loading, transcribing, cardTranscribing, messages])

  useEffect(() => {
    if (!ticket) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (zoom) setZoom(null)
        else if (editingTitle || editingDesc) {
          setEditingTitle(false)
          setEditingDesc(false)
        } else onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ticket, zoom, editingTitle, editingDesc, onClose])

  useEffect(() => {
    if (editingTitle && titleRef.current) {
      titleRef.current.focus()
      titleRef.current.setSelectionRange(0, titleRef.current.value.length)
    }
  }, [editingTitle])

  useEffect(() => {
    if (editingDesc && descRef.current) descRef.current.focus()
  }, [editingDesc])

  // ── Reply (WhatsApp) computed values ───────────────────────────────
  // MUST stay above the early `return null` below so the hook order is
  // stable across renders (React error #310 otherwise). All null-safe.
  // Most recent inbound message — every reply sent from this card quotes
  // it (WhatsApp threads the reply to the contact's last message), so the
  // user never has to pick a target manually.
  const latestInbound = useMemo(() => {
    let last: Message | null = null
    for (const m of messages) if (m.direction === 'inbound') last = m
    return last
  }, [messages])

  // Permission to reply (mirrors the d360-send server check).
  const canReply = useMemo(() => {
    if (!ticket || !project) return false
    const isOwner = currentUserId === project.user_id
    const isAssignee =
      !!ticket.assignee_id && ticket.assignee_id === currentUserId
    if (isOwner || isAssignee) return true
    const perm = project.reply_permission ?? 'owner'
    if (perm === 'subscribers') return subscriberIds?.has(currentUserId) ?? false
    if (perm === 'all_members') return subscribableUserIds.includes(currentUserId)
    return false
  }, [ticket, project, currentUserId, subscriberIds, subscribableUserIds])

  // Whether the conversation is still awaiting a reply: the most recent
  // message in the thread is inbound (the contact spoke last). We only
  // surface AI suggestions for unanswered cards.
  const unanswered = useMemo(() => {
    const last = messages.length ? messages[messages.length - 1] : null
    return last?.direction === 'inbound'
  }, [messages])

  // Auto-load reply suggestions whenever the Messagerie tab is shown for
  // an unanswered card — including when the user opens the tab directly
  // (the header « Répondre » button calls startReply(), but a plain tab
  // click would otherwise leave the suggestions empty). Respects the
  // project's suggestions toggle and the 24h window.
  useEffect(() => {
    if (activeTab !== 'messagerie') return
    if (!ticket || !canReply || !unanswered || !latestInbound) return
    if (project && project.reply_suggestions_enabled === false) return
    const expired =
      lastInboundAt == null || Date.now() - lastInboundAt > 24 * 60 * 60 * 1000
    if (expired) return
    void fetchReplySuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    ticket?.id,
    latestInbound?.id,
    unanswered,
    canReply,
    lastInboundAt,
    project?.reply_suggestions_enabled,
  ])

  // Drop any pending message selection when leaving the Messagerie tab or
  // switching cards. MUST stay above the early `return null` below so the
  // hook order is consistent across renders.
  useEffect(() => {
    setSelectMode(false)
    setSelectedMsgIds(new Set())
    setOpenCtl(null)
  }, [activeTab, ticket?.id])

  // Auto-publish a comment when the user drops a file with no caption — so
  // closing the card without clicking Publier still saves the attachment.
  // Reschedules whenever the staged list / draft text changes (typing a
  // caption cancels the auto-publish; typing then clearing reschedules it).
  // Hooks rule: declare before any early return.
  useEffect(() => {
    if (autoPostTimerRef.current) {
      window.clearTimeout(autoPostTimerRef.current)
      autoPostTimerRef.current = null
      setAutoPostPending(false)
    }
    if (activeTab !== 'commentaires') return
    if (commentDraft.trim().length > 0) return
    if (commentAttachments.length === 0) return
    if (commentAttachments.some((a) => a.uploading)) return
    if (!commentAttachments.some((a) => a.url && !a.error)) return
    setAutoPostPending(true)
    autoPostTimerRef.current = window.setTimeout(() => {
      autoPostTimerRef.current = null
      setAutoPostPending(false)
      void postComment()
    }, 1500)
    return () => {
      if (autoPostTimerRef.current) {
        window.clearTimeout(autoPostTimerRef.current)
        autoPostTimerRef.current = null
        setAutoPostPending(false)
      }
    }
    // postComment captures fresh state through its own setters; we only
    // schedule based on the visible inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, commentAttachments, commentDraft])

  if (!ticket) return null

  async function changeList(targetListId: string) {
    if (targetListId === ticket!.list_id) return
    const fromDone = lists.find((l) => l.id === ticket!.list_id)?.is_done ?? false
    const toDone = lists.find((l) => l.id === targetListId)?.is_done ?? false
    const patch: Partial<Ticket> = { list_id: targetListId }
    if (toDone && !fromDone) patch.done_at = new Date().toISOString()
    if (!toDone && fromDone) patch.done_at = null
    await onUpdate(ticket!.id, patch)
  }

  async function saveTitle() {
    const trimmed = titleDraft.trim()
    if (trimmed && trimmed !== ticket!.title) {
      await onUpdate(ticket!.id, { title: trimmed })
    }
    setEditingTitle(false)
  }

  async function saveDesc() {
    const html = sanitizeRichHtml(descDraft)
    const empty = isRichEmpty(html)
    const stored = empty ? null : html
    if (stored !== (ticket!.description ?? null)) {
      await onUpdate(ticket!.id, { description: stored })
    }
    setEditingDesc(false)
  }

  async function deleteTicket() {
    const ok = await confirm({
      title: t('detail.confirmDeleteCardTitle'),
      message: t('detail.confirmDeleteCardMessage'),
      confirmLabel: 'Supprimer la carte',
    })
    if (!ok) return
    await onDelete(ticket!.id)
    onClose()
  }

  function setPriority(p: Priority | null) {
    if (p === (ticket!.priority ?? null)) return
    onUpdate(ticket!.id, { priority: p })
  }

  function addLabel() {
    const v = labelDraft.trim()
    setLabelDraft('')
    if (!v) return
    const existing = ticket!.labels ?? []
    if (existing.some((l) => l.toLowerCase() === v.toLowerCase())) return
    onUpdate(ticket!.id, { labels: [...existing, v] })
  }

  function removeLabel(label: string) {
    onUpdate(ticket!.id, {
      labels: (ticket!.labels ?? []).filter((l) => l !== label),
    })
  }

  // ── Schedule (date / period) ──────────────────────────────────────────
  // Native date/time inputs are local; we serialise back to ISO. All-day
  // dates are pinned to local NOON so they never drift a day across time
  // zones / DST when rendered or exported.
  function isoToDateInput(iso: string | null): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  function isoToTimeInput(iso: string | null): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }
  function combineToIso(dateStr: string, timeStr: string, allDay: boolean): string | null {
    if (!dateStr) return null
    const [y, mo, da] = dateStr.split('-').map(Number)
    if (allDay) return new Date(y, mo - 1, da, 12, 0, 0).toISOString()
    const [h, mi] = (timeStr || '00:00').split(':').map(Number)
    return new Date(y, mo - 1, da, h || 0, mi || 0, 0).toISOString()
  }
  function patchSchedule(patch: {
    startDate?: string
    startTime?: string
    endDate?: string
    endTime?: string
    allDay?: boolean
    clear?: boolean
  }) {
    if (patch.clear) {
      onUpdate(ticket!.id, {
        scheduled_start: null,
        scheduled_end: null,
        scheduled_all_day: true,
      })
      return
    }
    const allDay = patch.allDay ?? ticket!.scheduled_all_day
    const startDate = patch.startDate ?? isoToDateInput(ticket!.scheduled_start)
    const startTime = patch.startTime ?? isoToTimeInput(ticket!.scheduled_start)
    // endDate '' explicitly clears the period.
    const endDate =
      patch.endDate !== undefined ? patch.endDate : isoToDateInput(ticket!.scheduled_end)
    const endTime = patch.endTime ?? isoToTimeInput(ticket!.scheduled_end)
    onUpdate(ticket!.id, {
      scheduled_start: combineToIso(startDate, startTime, allDay),
      scheduled_end: endDate ? combineToIso(endDate, endTime, allDay) : null,
      scheduled_all_day: allDay,
    })
  }

  function dateToInput(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  // Apply a calendar range selection (react-day-picker) to the schedule.
  function applyScheduleRange(range: DateRange | undefined) {
    if (!range?.from) {
      patchSchedule({ clear: true })
      return
    }
    const startDate = dateToInput(range.from)
    const endDate =
      range.to && dateToInput(range.to) !== startDate ? dateToInput(range.to) : ''
    patchSchedule({ startDate, endDate })
  }

  // 'HH:MM' (local) of the reminder's current anchor, or null if none set.
  function reminderTimeOf(tk: Ticket): string | null {
    if (!tk.reminder_anchor) return null
    const d = new Date(tk.reminder_anchor)
    if (isNaN(d.getTime())) return null
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  // Reminder = Google-Calendar-style recurrence (NO date needed). Enabling or
  // changing any field rebuilds the DTSTART anchor (today at the chosen time)
  // and recomputes the next fire time from the rule.
  function patchReminder(patch: {
    enabled?: boolean
    freq?: ReminderFreq
    interval?: number
    time?: string // 'HH:MM' fire time (daily/weekly/monthly/yearly)
    byweekday?: number[] // weekly weekdays (0=Sun…6=Sat)
    monthlyMode?: ReminderMonthlyMode
    untilDate?: string // '' clears the end
  }) {
    const tk = ticket!
    if (patch.enabled === false) {
      onUpdate(tk.id, { reminder_enabled: false, reminder_next_at: null })
      return
    }
    const freq = patch.freq ?? tk.reminder_freq ?? 'daily'
    const interval = patch.interval ?? tk.reminder_interval ?? 1
    const monthlyMode =
      patch.monthlyMode ?? tk.reminder_monthly_mode ?? 'day'
    const byweekday =
      patch.byweekday ??
      tk.reminder_byweekday ??
      [new Date().getDay()]
    // Fire time-of-day: from the patch, else the current anchor, else 09:00.
    const time = patch.time ?? reminderTimeOf(tk) ?? '09:00'
    const [hh, mm] = time.split(':').map((s) => parseInt(s, 10))

    // DTSTART = today at the chosen time (hourly anchors to "now").
    const anchor = new Date()
    if (freq === 'hourly') {
      anchor.setSeconds(0, 0)
    } else {
      anchor.setHours(
        Number.isFinite(hh) ? hh : 9,
        Number.isFinite(mm) ? mm : 0,
        0,
        0,
      )
    }
    const rule: ReminderRule = {
      freq,
      interval: Math.max(1, interval),
      byweekday: freq === 'weekly' ? byweekday.slice().sort((a, b) => a - b) : null,
      monthlyMode: freq === 'monthly' ? monthlyMode : null,
    }
    const until =
      patch.untilDate !== undefined
        ? patch.untilDate
          ? new Date(`${patch.untilDate}T23:59:59`).toISOString()
          : null
        : tk.reminder_until
    onUpdate(tk.id, {
      reminder_enabled: true,
      reminder_freq: freq,
      reminder_interval: Math.max(1, interval),
      reminder_anchor: anchor.toISOString(),
      reminder_byweekday: rule.byweekday,
      reminder_monthly_mode: rule.monthlyMode,
      reminder_next_at: nextReminderAfter(anchor.toISOString(), rule, Date.now()),
      reminder_until: until,
    })
  }

  function archiveTicket() {
    onArchive(ticket!.id)
    onClose()
  }

  /** Toggle an emoji "reaction" tag on a message. Shared across the team —
   *  the toggle_message_tag RPC handles auth + the array mutation. We update
   *  local state optimistically and reconcile with the server's response. */
  async function toggleMessageTag(messageId: string, tag: string) {
    setMessages((arr) =>
      arr.map((m) => {
        if (m.id !== messageId) return m
        const cur = m.tags ?? []
        const next = cur.includes(tag)
          ? cur.filter((x) => x !== tag)
          : [...cur, tag]
        return { ...m, tags: next }
      }),
    )
    const { data, error } = await supabase.rpc('toggle_message_tag', {
      p_message_id: messageId,
      p_tag: tag,
    })
    if (error) {
      console.error('toggle_message_tag failed', error)
      const { data: row } = await supabase
        .from('messages')
        .select('tags')
        .eq('id', messageId)
        .maybeSingle()
      setMessages((arr) =>
        arr.map((m) =>
          m.id === messageId ? { ...m, tags: (row?.tags as string[]) ?? [] } : m,
        ),
      )
      return
    }
    setMessages((arr) =>
      arr.map((m) =>
        m.id === messageId ? { ...m, tags: (data as string[] | null) ?? [] } : m,
      ),
    )
  }

  /** Whisper (verbatim) + Claude (synthèse 1–2 phrases) sur un message audio. */
  async function transcribeMessage(m: Message) {
    setTranscribing(m.id)
    if (ticket) onTranscribingChange?.(ticket.id, true)
    try {
      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: { message_id: m.id, locale: i18n.language },
      })
      if (error) {
        let detail = error.message
        try {
          const ctx = (error as { context?: Response }).context
          const body =
            ctx && typeof ctx.json === 'function' ? await ctx.json() : null
          if (body?.error) detail = body.error
        } catch {
          /* keep generic */
        }
        throw new Error(detail)
      }
      const result = data as {
        transcription?: string
        summary?: string
        title?: string
        is_question?: boolean
        mentioned_date?: string | null
        is_urgent?: boolean
        error?: string
      }
      if (result.error) throw new Error(result.error)
      const transcription = result.transcription ?? ''
      const summary = result.summary ?? ''
      setMessages((arr) =>
        arr.map((x) =>
          x.id === m.id ? { ...x, transcription, summary: summary || null } : x,
        ),
      )
      // Apply the AI signals to the CARD (gated by the user's detection
      // prefs). Without this, auto-transcription detected a date / question /
      // urgency but silently dropped it — the card never showed it.
      if (ticket) {
        const patch: Partial<Ticket> = {}
        if (
          aiDetect.date &&
          typeof result.mentioned_date === 'string' &&
          result.mentioned_date.trim() &&
          !ticket.ai_mentioned_date
        ) {
          const md = result.mentioned_date.trim()
          patch.ai_mentioned_date = md
          // Auto-apply (no validation): set the schedule to the detected date
          // so it shows circled in the planning + as the card date chip.
          if (!ticket.scheduled_start) {
            const [y, mo, d] = md.slice(0, 10).split('-').map(Number)
            if (y && mo && d) {
              patch.scheduled_start = new Date(y, mo - 1, d, 12, 0, 0).toISOString()
              patch.scheduled_end = null
              patch.scheduled_all_day = true
            }
          }
        }
        if (aiDetect.question && result.is_question === true && !ticket.ai_is_question) {
          patch.ai_is_question = true
        }
        if (aiDetect.urgent && result.is_urgent === true && !ticket.ai_is_urgent) {
          patch.ai_is_urgent = true
          if (aiDetect.autoPriority && !ticket.priority) patch.priority = 'urgent'
        }
        // Enrich a placeholder voice/video/doc card (title set by ingest to
        // « Message vocal · … ») with the transcription's title + summary.
        const isPlaceholder = /^(Message vocal|Vidéo|Document|Voice message|Video)\b/.test(
          ticket.title ?? '',
        )
        if (isPlaceholder) {
          const aiTitle = (result.title ?? '').trim()
          if (aiTitle) patch.title = aiTitle
          if (summary.trim()) patch.description = summary.trim()
        }
        if (Object.keys(patch).length > 0) await onUpdate(ticket.id, patch)
      }
    } catch (e) {
      console.error('transcription failed', e)
      notify({
        title: t('detail.notify.transcribeError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setTranscribing(null)
      if (ticket) onTranscribingChange?.(ticket.id, false)
    }
  }

  /** Trigger holistic analysis on the whole card. The proposal lives on
   *  `tickets.proposed_splits` and re-rendering picks it up via the prop. */
  async function analyzeForTicketSplit() {
    if (!ticket) return
    setAnalyzingTicket(true)
    try {
      const splits = await onAnalyzeSplit(ticket.id)
      if (splits.length === 0) {
        notify({
          title: t('detail.notify.analyzeDone'),
          message: t('detail.notify.analyzeDoneEmpty'),
          tone: 'info',
        })
      }
      // The ticket prop will refresh via realtime UPDATE on tickets.proposed_splits.
    } catch (e) {
      console.error('analyze ticket failed', e)
      notify({
        title: t('detail.notify.analyzeError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setAnalyzingTicket(false)
    }
  }

  async function applyTicketSplit(splits: ProposedSplit[]) {
    if (!ticket) return
    setApplyingSplit(true)
    try {
      await onApplySplit(ticket, splits)
      // Parent ticket is archived — close the modal (it's no longer on the board).
      onClose()
    } catch (e) {
      console.error('apply ticket split failed', e)
      notify({
        title: t('detail.notify.splitError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setApplyingSplit(false)
    }
  }

  async function dismissTicketSplit() {
    if (!ticket) return
    try {
      await onDismissSplit(ticket.id)
    } catch (e) {
      console.error('dismiss ticket split failed', e)
    }
  }

  // ── Manual message split (Messagerie tab) ─────────────────────────────
  function toggleMsgSelected(id: string) {
    setSelectedMsgIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function cancelMessageSplit() {
    setSelectMode(false)
    setSelectedMsgIds(new Set())
  }
  async function confirmMessageSplit() {
    if (!ticket || selectedMsgIds.size === 0 || splittingMessages) return
    setSplittingMessages(true)
    try {
      await onSplitMessagesToCard(ticket, Array.from(selectedMsgIds))
      // No success modal (consistent with the rest of the app) — the new
      // card just appears in « Reçu ». Just leave selection mode.
      cancelMessageSplit()
    } catch (e) {
      notify({
        title: t('detail.split.error'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setSplittingMessages(false)
    }
  }

  /** Ajoute du texte (résumé ou verbatim) à la description de la carte. */
  function addTextToDesc(text: string) {
    const trimmed = text.trim()
    if (!trimmed) return
    const base = ticket!.description?.trim()
    onUpdate(ticket!.id, { description: base ? `${base}\n\n${trimmed}` : trimmed })
  }

  /** Post a new internal comment, optimistically inserted into the list,
   *  then dispatch the email notification to the OTHER subscribers
   *  (the dispatcher always skips the actor). */
  async function postComment() {
    if (!ticket) return
    // RichEditor stores HTML; we sanitize on read AND on save, then derive a
    // plain-text version for the email notification (notify-event renders it
    // inside an italic <blockquote>, so we want clean text there).
    const html = sanitizeRichHtml(commentDraft)
    const plain = htmlToText(html)
    const readyMedia = commentAttachments
      .filter((a) => a.url && !a.error && !a.uploading)
      .map((a) => a.url as string)
    const stillUploading = commentAttachments.some((a) => a.uploading)
    if (stillUploading) {
      notify({ title: t('reply.attachStillUploading'), tone: 'info' })
      return
    }
    if (!plain.trim() && readyMedia.length === 0) return
    if (plain.length > 5000) {
      notify({
        title: t('detail.comments.tooLongTitle'),
        message: t('detail.comments.tooLongMessage'),
        tone: 'error',
      })
      return
    }
    setPostingComment(true)
    try {
      const { data, error } = await supabase
        .from('card_comments')
        .insert({
          ticket_id: ticket.id,
          author_id: currentUserId,
          body: html,
          media_urls: readyMedia,
        })
        .select()
        .single()
      if (error) throw error
      const inserted = data as CardComment
      setComments((arr) =>
        arr.some((c) => c.id === inserted.id)
          ? arr
          : [...arr, inserted].sort((a, b) =>
              a.created_at.localeCompare(b.created_at),
            ),
      )
      setCommentDraft('')
      setCommentAttachments([])
      // Fire-and-forget email to the other subscribers (the dispatcher
      // resolves recipients, filters by prefs, and excludes the actor).
      void notifyEvent({
        type: 'card_comment',
        ticket_id: ticket.id,
        comment_body: plain,
      })
    } catch (e) {
      console.error('post comment failed', e)
      notify({
        title: t('detail.comments.postError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setPostingComment(false)
    }
  }

  function startEditComment(c: CardComment) {
    setEditingCommentId(c.id)
    setEditingCommentDraft(c.body)
  }

  function cancelEditComment() {
    setEditingCommentId(null)
    setEditingCommentDraft('')
  }

  async function saveEditComment(id: string) {
    const next = sanitizeRichHtml(editingCommentDraft)
    if (isRichEmpty(next)) return
    setSavingCommentEdit(true)
    try {
      const { data, error } = await supabase
        .from('card_comments')
        .update({ body: next })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      const updated = data as CardComment
      setComments((arr) => arr.map((c) => (c.id === id ? updated : c)))
      setEditingCommentId(null)
      setEditingCommentDraft('')
    } catch (e) {
      console.error('edit comment failed', e)
      notify({
        title: t('detail.comments.editError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setSavingCommentEdit(false)
    }
  }

  /** Soft-delete: stamps deleted_at so other project members keep
   *  seeing a "Comment deleted" placeholder. The body stays in DB for
   *  audit but the UI no longer renders it. */
  async function deleteComment(c: CardComment) {
    const ok = await confirm({
      title: t('detail.comments.confirmDeleteTitle'),
      message: t('detail.comments.confirmDeleteMessage'),
      confirmLabel: t('common.delete'),
    })
    if (!ok) return
    try {
      const { data, error } = await supabase
        .from('card_comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', c.id)
        .select()
        .single()
      if (error) throw error
      const updated = data as CardComment
      setComments((arr) => arr.map((x) => (x.id === c.id ? updated : x)))
    } catch (e) {
      console.error('delete comment failed', e)
      notify({
        title: t('detail.comments.deleteError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    }
  }

  const currentList = lists.find((l) => l.id === ticket.list_id)

  // 24h window: null lastInbound = never received → can't open a chat.
  const windowExpired =
    lastInboundAt == null || Date.now() - lastInboundAt > 24 * 60 * 60 * 1000

  async function fetchReplySuggestions(toneOverride?: ReplyTone) {
    setReplySuggLoading(true)
    setReplySuggestions([])
    try {
      const { data, error } = await supabase.functions.invoke('suggest-replies', {
        body: {
          ticket_id: ticket!.id,
          locale: i18n.language,
          ...(toneOverride ? { tone: toneOverride } : {}),
        },
      })
      if (error) return
      const result = data as { suggestions?: string[] }
      setReplySuggestions(result.suggestions ?? [])
    } catch {
      /* silent — suggestions are a nicety */
    } finally {
      setReplySuggLoading(false)
    }
  }

  // Only the project owner may retune the channel's register / toggle
  // suggestions from the card (mirrors the project-settings gate).
  const isProjectOwner = !!project && currentUserId === project.user_id
  const suggestionsEnabled = project?.reply_suggestions_enabled !== false
  const currentTone: ReplyTone = project?.reply_tone ?? 'vous_pro'

  /** Live tone change from the messagerie : persist the new register on
   *  the project AND immediately regenerate the 3 suggestions with it
   *  (override avoids a read race with the optimistic patch). */
  function changeTone(next: ReplyTone) {
    if (!project || !onPatchProject || next === currentTone) return
    void onPatchProject(project.id, { reply_tone: next })
    if (canReply && !windowExpired && unanswered && suggestionsEnabled) {
      void fetchReplySuggestions(next)
    }
  }

  /** Toggle « analyse / suggestions » live from the messagerie. */
  function toggleSuggestions() {
    if (!project || !onPatchProject) return
    const next = !suggestionsEnabled
    void onPatchProject(project.id, { reply_suggestions_enabled: next })
    if (next) {
      if (canReply && !windowExpired && unanswered) void fetchReplySuggestions()
    } else {
      setReplySuggestions([])
      setTonePopoverOpen(false)
    }
  }

  /** Jump to the Messagerie tab, load AI suggestions for the contact's
   *  last message, and focus the composer. */
  function startReply() {
    if (!canReply || windowExpired) return
    setActiveTab('messagerie')
    if (latestInbound) void fetchReplySuggestions()
    window.setTimeout(() => replyComposerRef.current?.focus(), 60)
  }

  // ── Composer attachments (files + recorded audio) ───────────────────
  /** Upload one file to the public `media` bucket and track it as a
   *  pending attachment (optimistic chip → fills in url when uploaded). */
  async function stageFile(file: File, opts?: { recorded?: boolean }) {
    if (!ticket) return
    const localId = crypto.randomUUID()
    const ext = file.name.split('.').pop()?.toLowerCase() || extForMime(file.type)
    const kind = mimeToKind(file.type || '', ext)
    setPendingAttachments((arr) => [
      ...arr,
      {
        id: localId,
        url: null,
        kind,
        filename: file.name,
        mime: file.type || 'application/octet-stream',
        uploading: true,
        recorded: opts?.recorded,
        transcribe: false,
      },
    ])
    try {
      const path = `outbound/${ticket.id}/${localId}.${ext}`
      const { error } = await supabase.storage
        .from('media')
        .upload(path, file, {
          contentType: file.type || 'application/octet-stream',
          upsert: true,
        })
      if (error) throw error
      const { data } = supabase.storage.from('media').getPublicUrl(path)
      setPendingAttachments((arr) =>
        arr.map((a) =>
          a.id === localId ? { ...a, url: data.publicUrl, uploading: false } : a,
        ),
      )
    } catch (e) {
      console.error('attachment upload failed', e)
      setPendingAttachments((arr) =>
        arr.map((a) =>
          a.id === localId ? { ...a, uploading: false, error: true } : a,
        ),
      )
      notify({ title: t('reply.attachUploadError'), tone: 'error' })
    }
  }

  function onFilesPicked(files: FileList | null) {
    if (!files) return
    for (const f of Array.from(files).slice(0, 10)) void stageFile(f)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  /** Upload one file for an internal comment. Lives under a comments/
   *  prefix in the same public `media` bucket so RLS / CDN behave the
   *  same as reply attachments. Tracked in commentAttachments until the
   *  comment is posted, then cleared. */
  async function stageCommentFile(file: File) {
    if (!ticket) return
    const localId = crypto.randomUUID()
    const ext = file.name.split('.').pop()?.toLowerCase() || extForMime(file.type)
    const kind = mimeToKind(file.type || '', ext)
    setCommentAttachments((arr) => [
      ...arr,
      {
        id: localId,
        url: null,
        kind,
        filename: file.name,
        mime: file.type || 'application/octet-stream',
        uploading: true,
      },
    ])
    try {
      const path = `comments/${ticket.id}/${localId}.${ext}`
      const { error } = await supabase.storage
        .from('media')
        .upload(path, file, {
          contentType: file.type || 'application/octet-stream',
          upsert: true,
        })
      if (error) throw error
      const { data } = supabase.storage.from('media').getPublicUrl(path)
      setCommentAttachments((arr) =>
        arr.map((a) =>
          a.id === localId ? { ...a, url: data.publicUrl, uploading: false } : a,
        ),
      )
    } catch (e) {
      console.error('comment attachment upload failed', e)
      setCommentAttachments((arr) =>
        arr.map((a) =>
          a.id === localId ? { ...a, uploading: false, error: true } : a,
        ),
      )
      notify({ title: t('reply.attachUploadError'), tone: 'error' })
    }
  }

  function onCommentFilesPicked(files: FileList | null) {
    if (!files) return
    for (const f of Array.from(files).slice(0, 10)) void stageCommentFile(f)
    if (commentFileInputRef.current) commentFileInputRef.current.value = ''
  }

  function removeCommentAttachment(id: string) {
    setCommentAttachments((arr) => arr.filter((a) => a.id !== id))
  }

  // Drag-and-drop handlers for the comment composer — mirror the reply
  // composer's logic (depth counter avoids flicker over child elements).
  function onCommentDragEnter(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    commentDragDepthRef.current++
    setIsDraggingCommentFiles(true)
  }
  function onCommentDragOver(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }
  function onCommentDragLeave() {
    commentDragDepthRef.current = Math.max(0, commentDragDepthRef.current - 1)
    if (commentDragDepthRef.current === 0) setIsDraggingCommentFiles(false)
  }
  function onCommentDrop(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    commentDragDepthRef.current = 0
    setIsDraggingCommentFiles(false)
    onCommentFilesPicked(e.dataTransfer.files)
  }

  /** Upload one file to the public `media` bucket under cards/{id}/… and
   *  append its public URL straight to ticket.media_urls — so it shows up
   *  in the hero media section without any extra "publish" step. */
  async function stageCardFile(file: File) {
    if (!ticket) return
    const localId = crypto.randomUUID()
    const ext = file.name.split('.').pop()?.toLowerCase() || extForMime(file.type)
    setCardUploads((arr) => [
      ...arr,
      { id: localId, filename: file.name, uploading: true },
    ])
    try {
      const path = `cards/${ticket.id}/${localId}.${ext}`
      const { error } = await supabase.storage
        .from('media')
        .upload(path, file, {
          contentType: file.type || 'application/octet-stream',
          upsert: true,
        })
      if (error) throw error
      const { data } = supabase.storage.from('media').getPublicUrl(path)
      const cur = ticket!.media_urls ?? []
      await onUpdate(ticket!.id, { media_urls: [...cur, data.publicUrl] })
      setCardUploads((arr) => arr.filter((a) => a.id !== localId))
    } catch (e) {
      console.error('card media upload failed', e)
      setCardUploads((arr) =>
        arr.map((a) =>
          a.id === localId ? { ...a, uploading: false, error: true } : a,
        ),
      )
      notify({ title: t('reply.attachUploadError'), tone: 'error' })
    }
  }

  async function onCardFilesPicked(files: FileList | null) {
    if (!files) return
    // Sequential upload — two parallel uploads would each read the same
    // baseline ticket.media_urls and the second would clobber the first.
    for (const f of Array.from(files).slice(0, 10)) {
      await stageCardFile(f)
    }
    if (cardFileInputRef.current) cardFileInputRef.current.value = ''
  }

  function removeCardUpload(id: string) {
    setCardUploads((arr) => arr.filter((a) => a.id !== id))
  }

  function onCardDragEnter(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    cardDragDepthRef.current++
    setIsDraggingCardFiles(true)
  }
  function onCardDragOver(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }
  function onCardDragLeave() {
    cardDragDepthRef.current = Math.max(0, cardDragDepthRef.current - 1)
    if (cardDragDepthRef.current === 0) setIsDraggingCardFiles(false)
  }
  function onCardDrop(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    cardDragDepthRef.current = 0
    setIsDraggingCardFiles(false)
    void onCardFilesPicked(e.dataTransfer.files)
  }

  // ── Drag-and-drop into the composer ──────────────────────────────────
  // dragenter / dragleave fire on EVERY child crossing, so we keep a depth
  // counter to ignore intermediate transitions and only flip the highlight
  // when the cursor truly leaves the drop zone.
  function isFileDrag(e: React.DragEvent): boolean {
    // Some browsers don't populate `types` with 'Files' for in-page drag
    // sources (e.g. images dragged from another tab) but `items`/`files`
    // are still populated. Accept any of those signals so we never let a
    // file drop slip through to the default browser behaviour.
    const types = Array.from(e.dataTransfer?.types ?? [])
    if (types.includes('Files')) return true
    const items = e.dataTransfer?.items
    if (items && Array.from(items).some((i) => i.kind === 'file')) return true
    if ((e.dataTransfer?.files?.length ?? 0) > 0) return true
    return false
  }
  function onComposerDragEnter(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    dragDepthRef.current++
    setIsDraggingFiles(true)
  }
  function onComposerDragOver(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    // preventDefault here is what enables the drop event to fire at all.
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }
  function onComposerDragLeave() {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setIsDraggingFiles(false)
  }
  function onComposerDrop(e: React.DragEvent) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    dragDepthRef.current = 0
    setIsDraggingFiles(false)
    onFilesPicked(e.dataTransfer.files)
  }

  function removeAttachment(id: string) {
    setPendingAttachments((arr) => arr.filter((a) => a.id !== id))
  }

  function toggleAttachmentTranscribe(id: string) {
    setPendingAttachments((arr) =>
      arr.map((a) => (a.id === id ? { ...a, transcribe: !a.transcribe } : a)),
    )
  }

  async function startRecording() {
    if (recording) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mime = pickRecordingMime()
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
      recordChunksRef.current = []
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data)
      }
      rec.onstop = () => {
        stream.getTracks().forEach((tk) => tk.stop())
        const type = rec.mimeType || mime || 'audio/webm'
        const blob = new Blob(recordChunksRef.current, { type })
        const ext = extForMime(type)
        const file = new File([blob], `vocal-${Date.now()}.${ext}`, { type })
        void stageFile(file, { recorded: true })
      }
      rec.start()
      mediaRecorderRef.current = rec
      setRecording(true)
      setRecordSecs(0)
      recordTimerRef.current = window.setInterval(
        () => setRecordSecs((s) => s + 1),
        1000,
      )
    } catch {
      notify({ title: t('reply.micDenied'), tone: 'error' })
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
    setRecording(false)
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current)
      recordTimerRef.current = null
    }
  }

  /** Fire-and-forget Whisper transcription of an outbound audio message,
   *  patching the local thread with the result when it returns. */
  async function transcribeOutbound(messageId: string) {
    try {
      const { data } = await supabase.functions.invoke('transcribe-audio', {
        body: { message_id: messageId },
      })
      const r = data as { transcription?: string }
      if (r?.transcription) {
        setMessages((arr) =>
          arr.map((m) =>
            m.id === messageId ? { ...m, transcription: r.transcription! } : m,
          ),
        )
      }
    } catch {
      /* transcription is optional — ignore failures */
    }
  }

  /** Insert an emoji into the reply composer at the current caret position
   *  (or at the end if the textarea isn't focused). Restores focus + caret
   *  right after the inserted glyph so the user can keep typing. */
  function insertEmojiInReply(emoji: string) {
    const ta = replyComposerRef.current
    if (!ta) {
      setReplyDraft((d) => d + emoji)
      return
    }
    const start = ta.selectionStart ?? ta.value.length
    const end = ta.selectionEnd ?? ta.value.length
    const next = ta.value.slice(0, start) + emoji + ta.value.slice(end)
    setReplyDraft(next)
    // After React applies the new value, restore caret + focus.
    window.setTimeout(() => {
      if (!replyComposerRef.current) return
      const caret = start + emoji.length
      replyComposerRef.current.focus()
      replyComposerRef.current.setSelectionRange(caret, caret)
    }, 0)
  }

  /** AI spelling / grammar pass on the draft (complements the browser's
   *  native live check). Fixes mistakes without touching the meaning,
   *  language or register, then replaces the composer text. */
  async function correctDraft() {
    const text = replyDraft.trim()
    if (!text || correcting || sendingReply) return
    setCorrecting(true)
    try {
      const { data, error } = await supabase.functions.invoke('correct-text', {
        body: { text: replyDraft, locale: i18n.language },
      })
      if (error) throw error
      const corrected = (data as { corrected?: string }).corrected ?? ''
      if (corrected && corrected.trim() !== text) {
        setReplyDraft(corrected)
        replyComposerRef.current?.focus()
      } else {
        notify({ title: t('reply.correctNoChange'), tone: 'info' })
      }
    } catch (e) {
      notify({
        title: t('reply.correctError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setCorrecting(false)
    }
  }

  async function sendReply() {
    if (!ticket || sendingReply || !canReply || windowExpired) return
    const text = replyDraft.trim()
    // Only send attachments that finished uploading without error.
    const ready = pendingAttachments.filter((a) => a.url && !a.error && !a.uploading)
    const stillUploading = pendingAttachments.some((a) => a.uploading)
    if (stillUploading) {
      notify({ title: t('reply.attachStillUploading'), tone: 'info' })
      return
    }
    if (!text && ready.length === 0) return
    setSendingReply(true)
    try {
      const { data, error } = await supabase.functions.invoke('d360-send', {
        body: {
          ticket_id: ticket.id,
          body: text,
          // Always quote the contact's latest message (WhatsApp threads
          // the reply to it). No manual target picking.
          reply_to_message_id: latestInbound?.id ?? null,
          attachments: ready.map((a) => ({
            url: a.url,
            type: a.kind,
            filename: a.filename,
          })),
        },
      })
      if (error) {
        let code: string | null = null
        let detail = error.message
        try {
          const ctx = (error as { context?: Response }).context
          const b = ctx && typeof ctx.json === 'function' ? await ctx.json() : null
          if (b?.code) code = b.code
          if (b?.error) detail = b.error
        } catch {
          /* keep generic */
        }
        if (code === 'window_expired') {
          setLastInboundAt(0)
          notify({ title: t('reply.windowExpired'), message: t('reply.windowExpiredHint'), tone: 'error' })
          return
        }
        if (code === 'not_allowed') {
          notify({ title: t('reply.noPermission'), message: t('reply.noPermissionHint'), tone: 'error' })
          return
        }
        throw new Error(detail)
      }
      const result = data as {
        ok?: boolean
        messages?: Message[]
        message?: Message
        partial?: boolean
        error?: string
      }
      const sent = result.messages ?? (result.message ? [result.message] : [])
      if (!result.ok || sent.length === 0) {
        throw new Error(result.error ?? 'send failed')
      }
      // Append all outbound messages to the loaded thread.
      setMessages((arr) => [...arr, ...sent])
      // Kick off Whisper transcription for the recorded vocals the user
      // asked to transcribe (matched to their created message by URL).
      for (const a of ready) {
        if (a.kind === 'audio' && a.transcribe) {
          const created = sent.find((m) => (m.media_urls ?? []).includes(a.url!))
          if (created) void transcribeOutbound(created.id)
        }
      }
      if (result.partial) {
        notify({ title: t('reply.sendPartial'), tone: 'warning' })
      }
      // Clear the composer.
      setReplyDraft('')
      setReplySuggestions([])
      setPendingAttachments([])
    } catch (e) {
      notify({
        title: t('reply.sendErrorToast'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setSendingReply(false)
    }
  }

  // Worth proposing the AI split analysis only if there's actual content to
  // analyze: a substantial text body, an existing transcription, or at least
  // one audio source (we'll auto-transcribe at analysis time). Cards with
  // just images or empty bodies hide the section entirely.
  const hasAnalyzableContent = messages.some((m) => {
    const body = m.body?.trim() ?? ''
    const transcription = m.transcription?.trim() ?? ''
    const mm = groupMedia(m.media_urls)
    return body.length > 50 || transcription.length > 50 || mm.audio.length > 0
  })

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden bg-slate-900/30 px-4 py-6 backdrop-blur-sm"
        onClick={onClose}
      >
        {/* V3 media-first shell — 900px, hero bar + (main | rail) grid + footer. */}
        <div
          className="flex h-[88vh] w-full max-w-[900px] flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── HERO BAR ─────────────────────────────────────────────── */}
          <div className="flex shrink-0 items-center gap-3 border-b border-[var(--color-border-soft)] px-5 py-2.5">
            {/* Favorite toggle — top-left (replaces the old label icon). */}
            <button
              type="button"
              onClick={() => onUpdate(ticket.id, { favorite: !ticket.favorite })}
              title={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              aria-label={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors',
                ticket.favorite
                  ? 'text-amber-500 hover:bg-amber-500/10'
                  : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
              )}
            >
              <Star
                className="h-4 w-4"
                fill={ticket.favorite ? 'currentColor' : 'none'}
                strokeWidth={ticket.favorite ? 1.5 : 2.5}
              />
            </button>
            <div className="min-w-0 flex-1">
              {editingTitle ? (
                <textarea
                  ref={titleRef}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      saveTitle()
                    }
                  }}
                  rows={1}
                  className="block w-full resize-none rounded-lg border border-[var(--color-link)] bg-[var(--color-surface)] px-2 py-1 text-[14px] font-semibold leading-snug text-[var(--color-text-strong)] outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingTitle(true)}
                  className="block w-full truncate rounded-lg px-2 py-1 -mx-2 text-left text-[14px] font-semibold leading-snug text-[var(--color-text-strong)] transition-colors hover:bg-[var(--color-surface-2)]"
                  title={t('detail.clickToEdit')}
                >
                  {ticket.title}
                </button>
              )}
              {/* Workflow controls — Statut / Priorité / Membres / Étiquettes
                  as compact top-banner popovers (replaces the old right rail). */}
              <div className="mt-1 flex flex-wrap items-center gap-1.5 px-2 text-[12px] text-[var(--color-text-dim)]">
                {/* ── Statut ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'status' ? null : 'status')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 font-medium transition-colors',
                      openCtl === 'status'
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                        : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', currentList ? listDotClass(currentList) : 'bg-[var(--color-text-dim)]')} />
                    <span className="max-w-[140px] truncate">{currentList ? displayListName(currentList) : t('detail.statusLabel')}</span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'status' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      <div className="absolute left-0 top-full z-[56] mt-1 flex max-h-[280px] w-max min-w-[180px] max-w-[280px] flex-col gap-1 overflow-y-auto rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-modal)]">
                        {lists.map((l) => {
                          const active = ticket.list_id === l.id
                          return (
                            <button
                              key={l.id}
                              type="button"
                              onClick={() => { void changeList(l.id); setOpenCtl(null) }}
                              className={cn(
                                'inline-flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors',
                                active ? 'bg-[var(--color-surface-2)] text-[var(--color-text-strong)]' : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                              )}
                            >
                              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', listDotClass(l))} />
                              <span className="min-w-0 flex-1 truncate text-left">{displayListName(l)}</span>
                              {active && <Check className="h-3 w-3 shrink-0 text-[var(--color-text-dim)]" />}
                            </button>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Priorité ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'priority' ? null : 'priority')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 font-medium transition-colors',
                      openCtl === 'priority'
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                        : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    {ticket.priority === 'urgent' ? (
                      <Flame className="h-3.5 w-3.5 text-red-500" strokeWidth={2.4} />
                    ) : ticket.priority === 'high' ? (
                      <Flame className="h-3.5 w-3.5 text-orange-500" strokeWidth={2.4} />
                    ) : ticket.priority ? (
                      <span className={cn('h-1.5 w-1.5 rounded-full', PRIORITY_META[ticket.priority].dot)} />
                    ) : null}
                    <span className="truncate">{ticket.priority ? priorityLabel(ticket.priority) : t('cardMenu.priority')}</span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'priority' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      <div className="absolute left-0 top-full z-[56] mt-1 flex w-max min-w-[160px] flex-col gap-1 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-modal)]">
                        {PRIORITY_ORDER.map((p) => {
                          const meta = PRIORITY_META[p]
                          const active = ticket.priority === p
                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={() => { void setPriority(active ? null : p); setOpenCtl(null) }}
                              className={cn(
                                'inline-flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors',
                                active ? 'bg-[var(--color-surface-2)] text-[var(--color-text-strong)]' : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                              )}
                            >
                              {p === 'urgent' ? (
                                <Flame className="h-3.5 w-3.5 text-red-500" strokeWidth={2.4} />
                              ) : p === 'high' ? (
                                <Flame className="h-3.5 w-3.5 text-orange-500" strokeWidth={2.4} />
                              ) : (
                                <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                              )}
                              <span className="min-w-0 flex-1 truncate text-left">{priorityLabel(p)}</span>
                              {active && <Check className="h-3 w-3 shrink-0 text-[var(--color-text-dim)]" />}
                            </button>
                          )
                        })}
                        <button
                          type="button"
                          onClick={() => { void setPriority(null); setOpenCtl(null) }}
                          className={cn(
                            'inline-flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors',
                            ticket.priority == null ? 'bg-[var(--color-surface-2)] text-[var(--color-text-strong)]' : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)]',
                          )}
                        >
                          <span className="min-w-0 flex-1 text-left">{t('cardMenu.priorityNone')}</span>
                          {ticket.priority == null && <Check className="h-3 w-3 shrink-0 text-[var(--color-text-dim)]" />}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Membres ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'members' ? null : 'members')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 font-medium transition-colors',
                      openCtl === 'members'
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                        : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    {(() => {
                      const subs = subscribableUserIds.filter((uid) => subscriberIds?.has(uid))
                      if (subs.length === 0) return <UserPlus className="h-3.5 w-3.5" />
                      return (
                        <span className="flex -space-x-1.5">
                          {subs.slice(0, 3).map((uid) => (
                            <Avatar
                              key={uid}
                              profile={profilesById[uid]}
                              userId={uid}
                              className="h-4 w-4 ring-1 ring-[var(--color-surface)]"
                              textClassName="text-[7px]"
                            />
                          ))}
                          {subs.length > 3 && <span className="pl-2">+{subs.length - 3}</span>}
                        </span>
                      )
                    })()}
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'members' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      <div className="absolute left-0 top-full z-[56] mt-1 flex max-h-[280px] w-max min-w-[200px] max-w-[280px] flex-col gap-1 overflow-y-auto rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-modal)]">
                        {subscribableUserIds.map((uid) => {
                          const profile = profilesById[uid]
                          const isSub = subscriberIds?.has(uid) ?? false
                          const isMe = uid === currentUserId
                          const name = profileName(profile) || (isMe ? t('common.you') : t('topbar.members'))
                          return (
                            <button
                              key={uid}
                              type="button"
                              onClick={() => onToggleSubscriber(ticket.id, uid, !isSub)}
                              className={cn(
                                'inline-flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px] font-medium transition-colors',
                                isSub ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]' : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                              )}
                            >
                              <Avatar profile={profile} userId={uid} className="h-5 w-5" textClassName="text-[8px]" />
                              <span className="min-w-0 flex-1 truncate text-left">{name}</span>
                              {isSub && <Check className="h-3 w-3 shrink-0" />}
                            </button>
                          )
                        })}
                        <button
                          type="button"
                          onClick={() => { onOpenMembers(); setOpenCtl(null) }}
                          className="mt-0.5 inline-flex w-full items-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] px-2 py-1.5 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          {t('cardMenu.inviteToFollow')}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Étiquettes ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'labels' ? null : 'labels')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 font-medium transition-colors',
                      openCtl === 'labels'
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                        : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    <Tag className="h-3.5 w-3.5" />
                    <span className="truncate">
                      {ticket.labels.length > 0 ? `${t('cardMenu.labels')} · ${ticket.labels.length}` : t('cardMenu.labels')}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'labels' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      <div className="absolute left-0 top-full z-[56] mt-1 w-max min-w-[200px] max-w-[280px] rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2.5 shadow-[var(--shadow-modal)]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {ticket.labels.map((label) => (
                            <span
                              key={label}
                              className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset', labelColor(label))}
                            >
                              {label}
                              <button
                                type="button"
                                onClick={() => removeLabel(label)}
                                className="rounded-full p-0.5 transition-colors hover:bg-black/10"
                                aria-label={`${t('common.delete')} ${label}`}
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </span>
                          ))}
                          <input
                            value={labelDraft}
                            onChange={(e) => setLabelDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                addLabel()
                              }
                            }}
                            onBlur={addLabel}
                            placeholder={t('cardMenu.labelAddPlaceholder')}
                            className="min-w-[110px] flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[11px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                          />
                        </div>

                        {/* Existing project labels — one click to reapply.
                            Filtered against labels already on this card. */}
                        {(() => {
                          const cur = new Set(ticket.labels ?? [])
                          const suggestions = (projectLabels ?? []).filter(
                            (l) => !cur.has(l),
                          )
                          if (suggestions.length === 0) return null
                          return (
                            <div className="mt-2 border-t border-[var(--color-border-soft)] pt-2">
                              <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]">
                                {t('cardMenu.labelsExisting')}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {suggestions.map((label) => (
                                  <button
                                    key={label}
                                    type="button"
                                    onClick={() =>
                                      onUpdate(ticket.id, {
                                        labels: [...(ticket.labels ?? []), label],
                                      })
                                    }
                                    title={t('cardMenu.labelsReapply', { label })}
                                    aria-label={t('cardMenu.labelsReapply', { label })}
                                    className={cn(
                                      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium opacity-70 ring-1 ring-inset transition-opacity hover:opacity-100',
                                      labelColor(label),
                                    )}
                                  >
                                    + {label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Date / Période (icône seule) ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'date' ? null : 'date')}
                    title={
                      ticket.scheduled_start
                        ? formatScheduled(ticket.scheduled_start, ticket.scheduled_end, ticket.scheduled_all_day)
                        : t('detail.schedule.date')
                    }
                    aria-label={t('detail.schedule.date')}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-medium transition-colors',
                      ticket.scheduled_start
                        ? 'border-violet-300 bg-violet-50 text-violet-700'
                        : openCtl === 'date'
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                          : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'date' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      <div className="rdp-pop absolute left-0 top-full z-[56] mt-1 flex w-[300px] flex-col gap-2 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-3 text-[var(--color-text)] shadow-[var(--shadow-modal)]">
                        {/* The AI-detected date is auto-applied (no validation) —
                            it lands circled in the calendar below. */}
                        {/* Clean range calendar */}
                        <DayPicker
                          mode="range"
                          weekStartsOn={1}
                          locale={i18n.language?.startsWith('fr') ? dpFr : undefined}
                          selected={
                            ticket.scheduled_start
                              ? {
                                  from: new Date(ticket.scheduled_start),
                                  to: ticket.scheduled_end ? new Date(ticket.scheduled_end) : undefined,
                                }
                              : undefined
                          }
                          onSelect={applyScheduleRange}
                        />

                        {/* Selected period summary */}
                        {ticket.scheduled_start && (
                          <div className="rounded-lg bg-[var(--color-surface-2)] px-2.5 py-1.5 text-center text-[12px] font-medium text-[var(--color-text)]">
                            {formatScheduled(ticket.scheduled_start, ticket.scheduled_end, ticket.scheduled_all_day)}
                          </div>
                        )}

                        {/* All-day + time */}
                        <label className="flex items-center gap-2 text-[12px] font-medium">
                          <input
                            type="checkbox"
                            checked={ticket.scheduled_all_day}
                            onChange={(e) => patchSchedule({ allDay: e.target.checked })}
                            className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                          />
                          {t('detail.schedule.allDay')}
                        </label>
                        {!ticket.scheduled_all_day && ticket.scheduled_start && (
                          <div className="flex items-center gap-2 text-[12px]">
                            <input
                              type="time"
                              value={isoToTimeInput(ticket.scheduled_start)}
                              onChange={(e) => patchSchedule({ startTime: e.target.value })}
                              className="w-[88px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 outline-none focus:border-[var(--color-link)]"
                            />
                            {ticket.scheduled_end && (
                              <>
                                <span className="text-[var(--color-text-dim)]">→</span>
                                <input
                                  type="time"
                                  value={isoToTimeInput(ticket.scheduled_end)}
                                  onChange={(e) => patchSchedule({ endTime: e.target.value })}
                                  className="w-[88px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 outline-none focus:border-[var(--color-link)]"
                                />
                              </>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        {ticket.scheduled_start && (
                          <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border-soft)] pt-2">
                            <a
                              href={
                                googleCalendarUrl({
                                  title: ticket.title,
                                  details: htmlToText(ticket.description ?? ''),
                                  start: ticket.scheduled_start,
                                  end: ticket.scheduled_end,
                                  allDay: ticket.scheduled_all_day,
                                }) ?? '#'
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-2.5 py-1.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)]"
                            >
                              <CalendarDays className="h-3.5 w-3.5" />
                              {t('detail.schedule.addToGoogle')}
                            </a>
                            <button
                              type="button"
                              onClick={() => { patchSchedule({ clear: true }); setOpenCtl(null) }}
                              className="rounded-lg px-2 py-1.5 text-[11.5px] font-medium text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
                            >
                              {t('detail.schedule.clear')}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Rappel (cloche — périodicité) ── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenCtl(openCtl === 'reminder' ? null : 'reminder')}
                    title={t('detail.schedule.reminder.enable')}
                    aria-label={t('detail.schedule.reminder.enable')}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-medium transition-colors',
                      ticket.reminder_enabled
                        ? 'border-amber-300 bg-amber-50 text-amber-700'
                        : openCtl === 'reminder'
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                          : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    <Bell className="h-3.5 w-3.5" fill={ticket.reminder_enabled ? 'currentColor' : 'none'} />
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                  {openCtl === 'reminder' && (
                    <>
                      <div className="fixed inset-0 z-[55]" onClick={() => setOpenCtl(null)} />
                      {(() => {
                        const freq = ticket.reminder_freq ?? 'daily'
                        const interval = ticket.reminder_interval || 1
                        const today = new Date()
                        const selectedDays =
                          ticket.reminder_byweekday ?? [today.getDay()]
                        const monthlyMode = ticket.reminder_monthly_mode ?? 'day'
                        const time = reminderTimeOf(ticket) ?? '09:00'
                        // Narrow weekday labels (L M M J V S D…), Monday-first.
                        const narrow = new Intl.DateTimeFormat(i18n.language, {
                          weekday: 'narrow',
                        })
                        const long = new Intl.DateTimeFormat(i18n.language, {
                          weekday: 'long',
                        })
                        const dowLabel = (dow: number) =>
                          narrow.format(new Date(2024, 0, 7 + dow)) // 2024-01-07 = Sunday
                        const weekdayLong = (dow: number) =>
                          long.format(new Date(2024, 0, 7 + dow))
                        const weekOrder = [1, 2, 3, 4, 5, 6, 0]
                        const nthOfToday =
                          Math.floor((today.getDate() - 1) / 7) + 1
                        const ord = (n: number) =>
                          i18n.language.startsWith('fr')
                            ? n === 1
                              ? '1er'
                              : `${n}e`
                            : n === 1
                              ? '1st'
                              : n === 2
                                ? '2nd'
                                : n === 3
                                  ? '3rd'
                                  : `${n}th`
                        const toggleDay = (dow: number) => {
                          const set = new Set(selectedDays)
                          if (set.has(dow)) set.delete(dow)
                          else set.add(dow)
                          const next = Array.from(set)
                          patchReminder({
                            byweekday: next.length ? next : [today.getDay()],
                          })
                        }
                        return (
                      <div className="absolute left-0 top-full z-[56] mt-1 flex w-[256px] flex-col gap-2 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-3 text-[var(--color-text)] shadow-[var(--shadow-modal)]">
                        <label className="flex items-center gap-2 text-[12px] font-medium">
                          <input
                            type="checkbox"
                            checked={ticket.reminder_enabled}
                            onChange={(e) => patchReminder({ enabled: e.target.checked })}
                            className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                          />
                          <Bell className="h-3.5 w-3.5" />
                          {t('detail.schedule.reminder.enable')}
                        </label>
                        {ticket.reminder_enabled && (
                          <div className="flex flex-col gap-2 pl-1">
                            {/* Frequency + interval (« tous les N … ») */}
                            <div className="flex items-center gap-1.5 text-[12px]">
                              <span>{t('detail.schedule.reminder.every')}</span>
                              <input
                                type="number"
                                min={1}
                                value={interval}
                                onChange={(e) => patchReminder({ interval: Number(e.target.value) })}
                                className="w-12 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[12px] outline-none focus:border-[var(--color-link)]"
                              />
                              <select
                                value={freq}
                                onChange={(e) => patchReminder({ freq: e.target.value as ReminderFreq })}
                                className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[12px] outline-none focus:border-[var(--color-link)]"
                              >
                                <option value="hourly">{t('detail.schedule.reminder.unit.hourly', { count: interval })}</option>
                                <option value="daily">{t('detail.schedule.reminder.unit.daily', { count: interval })}</option>
                                <option value="weekly">{t('detail.schedule.reminder.unit.weekly', { count: interval })}</option>
                                <option value="monthly">{t('detail.schedule.reminder.unit.monthly', { count: interval })}</option>
                                <option value="yearly">{t('detail.schedule.reminder.unit.yearly', { count: interval })}</option>
                              </select>
                            </div>

                            {/* Weekly → weekday toggles */}
                            {freq === 'weekly' && (
                              <div className="flex flex-wrap gap-1">
                                {weekOrder.map((dow) => {
                                  const on = selectedDays.includes(dow)
                                  return (
                                    <button
                                      key={dow}
                                      type="button"
                                      onClick={() => toggleDay(dow)}
                                      title={weekdayLong(dow)}
                                      className={cn(
                                        'h-6 w-6 rounded-full text-[11px] font-semibold uppercase transition-colors',
                                        on
                                          ? 'bg-[var(--color-accent)] text-white'
                                          : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)]',
                                      )}
                                    >
                                      {dowLabel(dow)}
                                    </button>
                                  )
                                })}
                              </div>
                            )}

                            {/* Monthly → by day-of-month vs nth weekday */}
                            {freq === 'monthly' && (
                              <div className="flex flex-col gap-1">
                                <label className="flex items-center gap-2 text-[12px]">
                                  <input
                                    type="radio"
                                    name="monthlyMode"
                                    checked={monthlyMode === 'day'}
                                    onChange={() => patchReminder({ monthlyMode: 'day' })}
                                    className="accent-[var(--color-accent)]"
                                  />
                                  {t('detail.schedule.reminder.monthlyDay', { day: today.getDate() })}
                                </label>
                                <label className="flex items-center gap-2 text-[12px]">
                                  <input
                                    type="radio"
                                    name="monthlyMode"
                                    checked={monthlyMode === 'nth'}
                                    onChange={() => patchReminder({ monthlyMode: 'nth' })}
                                    className="accent-[var(--color-accent)]"
                                  />
                                  {t('detail.schedule.reminder.monthlyNth', {
                                    nth: ord(nthOfToday),
                                    weekday: weekdayLong(today.getDay()),
                                  })}
                                </label>
                              </div>
                            )}

                            {/* Time of day (all but hourly) */}
                            {freq !== 'hourly' && (
                              <label className="flex items-center gap-1.5 text-[12px]">
                                {t('detail.schedule.reminder.atTime')}
                                <input
                                  type="time"
                                  value={time}
                                  onChange={(e) => patchReminder({ time: e.target.value })}
                                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[12px] outline-none focus:border-[var(--color-link)]"
                                />
                              </label>
                            )}

                            {/* End */}
                            <label className="flex items-center gap-1.5 text-[12px]">
                              {t('detail.schedule.reminder.until')}
                              <input
                                type="date"
                                value={isoToDateInput(ticket.reminder_until)}
                                onChange={(e) => patchReminder({ untilDate: e.target.value })}
                                className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-[12px] outline-none focus:border-[var(--color-link)]"
                              />
                            </label>

                            {ticket.reminder_next_at && (
                              <p className="text-[11px] text-[var(--color-text-dim)]">
                                {t('detail.schedule.reminder.next', {
                                  date: formatScheduled(ticket.reminder_next_at, null, false),
                                })}
                              </p>
                            )}
                            <p className="text-[10.5px] text-[var(--color-text-dim)]">
                              {t('detail.schedule.reminder.emailNote')}
                            </p>
                          </div>
                        )}
                      </div>
                        )
                      })()}
                    </>
                  )}
                </div>

                {isTicketUrgent(ticket) && (
                  <button
                    type="button"
                    onClick={() =>
                      onUpdate(ticket.id, {
                        ai_is_urgent: false,
                        ...(ticket.priority === 'urgent' ? { priority: null } : {}),
                      })
                    }
                    className="group/urgent inline-flex items-center gap-1 rounded-md bg-[var(--color-danger-bg)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger)] hover:text-white"
                    title={t('cardMenu.clearUrgent')}
                  >
                    <Flame className="h-3 w-3 group-hover/urgent:hidden" strokeWidth={2.5} />
                    <X className="hidden h-3 w-3 group-hover/urgent:block" strokeWidth={2.5} />
                    {t('detail.chipUrgent')}
                  </button>
                )}
                {ticket.ai_is_question && (
                  <span
                    className="inline-flex items-center gap-1 rounded-md bg-[var(--color-warning-bg)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-warning)]"
                    title={t('detail.tooltipQuestion')}
                  >
                    <HelpCircle className="h-3 w-3" strokeWidth={2.5} />
                    {t('detail.chipQuestion')}
                  </span>
                )}
                {/* Read-only AI date chip — only when it hasn't been applied
                    to the schedule (else the Date control already shows it). */}
                {ticket.ai_mentioned_date && !ticket.scheduled_start && (
                  <span
                    className="inline-flex items-center gap-1 rounded-md bg-[var(--color-info-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-info)]"
                    title={formatAiDate(ticket.ai_mentioned_date)}
                  >
                    <CalendarDays className="h-3 w-3" strokeWidth={2.5} />
                    {formatAiDate(ticket.ai_mentioned_date)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {project && (
                <button
                  type="button"
                  onClick={startReply}
                  disabled={!canReply || windowExpired}
                  className="inline-flex h-[30px] items-center gap-1.5 rounded-[10px] bg-[var(--color-accent)] px-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    !canReply
                      ? t('reply.noPermission')
                      : windowExpired
                        ? t('reply.windowExpired')
                        : t('detail.replyTooltip')
                  }
                >
                  <Send className="h-3.5 w-3.5" />
                  {t('detail.reply')}
                </button>
              )}
              {onMoveCard && (
                <button
                  type="button"
                  onClick={() => onMoveCard(ticket)}
                  title={t('detail.moveTooltip')}
                  aria-label={t('detail.move')}
                  className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={archiveTicket}
                title={t('detail.archiveTooltip')}
                aria-label={t('detail.archive')}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
              >
                <Archive className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Audio analysis in progress — prominent, always-visible banner so
              the user knows the AI is transcribing/analysing (covers BOTH the
              auto-on-arrival/kebab path and the modal's own transcription). */}
          {(transcribing != null || cardTranscribing) && (
            <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-accent)]/20 bg-[var(--color-accent-bg)] px-5 py-2 text-[12px] font-medium text-[var(--color-accent-text)]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('detail.transcribingBanner')}
            </div>
          )}

          {/* ── BODY (single column — the right rail was removed; its
                controls now live in the header popovers) ───────────────── */}
          <div className="min-h-0 flex-1 overflow-hidden">
            {/* MAIN COLUMN (scrolls independently) */}
            <div className="h-full min-h-0 min-w-0 overflow-y-auto">
              {/* TAB BAR — Contenu / Messagerie / Commentaires */}
              <div className="flex items-center gap-1 border-b border-[var(--color-border-soft)] px-5 pt-3">
                {(
                  [
                    ['contenu', t('detail.tabContent'), 0],
                    ['messagerie', t('detail.tabMessaging'), messages.length],
                    ['commentaires', t('detail.comments.title'), comments.length],
                  ] as const
                ).map(([key, label, count]) => {
                  const active = activeTab === key
                  // Colour the « messagerie » + « commentaires » tabs to
                  // match their chips on the card: green for the WhatsApp
                  // conversation, sky-blue for the internal team comments.
                  // Contenu keeps the neutral accent.
                  const tabBorder =
                    key === 'messagerie'
                      ? 'border-emerald-500'
                      : key === 'commentaires'
                        ? 'border-sky-500'
                        : 'border-[var(--color-accent)]'
                  const tabIconColor =
                    key === 'messagerie'
                      ? active
                        ? 'text-emerald-600'
                        : 'text-emerald-500/70'
                      : key === 'commentaires'
                        ? active
                          ? 'text-sky-600'
                          : 'text-sky-500/70'
                        : ''
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-2 text-[12.5px] font-semibold transition-colors',
                        active
                          ? `${tabBorder} text-[var(--color-text-strong)]`
                          : 'border-transparent text-[var(--color-text-dim)] hover:text-[var(--color-text)]',
                      )}
                    >
                      {key === 'contenu' ? (
                        <AlignLeft className="h-4 w-4" />
                      ) : key === 'messagerie' ? (
                        <MessagesSquare className={cn('h-4 w-4', tabIconColor)} />
                      ) : (
                        <MessageCircleMore className={cn('h-4 w-4', tabIconColor)} />
                      )}
                      {label}
                      {count > 0 && (
                        <span className="text-[var(--color-text-dim)]">· {count}</span>
                      )}
                    </button>
                  )
                })}
              </div>

              {/* ── CONTENU : description first, then media + transcription ──
                  The description leads the tab; media + (collapsed) transcription
                  spoilers come after. We keep the JSX order but reorder visually
                  with flex `order-*` so the description is always on top. */}
              {activeTab === 'contenu' && (
                <div className="flex flex-col">
              {/* HERO MEDIA */}
              {(() => {
                const m = groupMedia(ticket.media_urls)
                const fileTotal =
                  m.image.length + m.video.length + m.audio.length + m.pdf.length + m.file.length
                if (fileTotal === 0) return null
                const hasAudio = m.audio.length > 0
                // When the card carries audio/video, the découpage action
                // lives inside each transcription spoiler (next to Régénérer),
                // so the section-level button is only shown for media without
                // a transcription block (images / PDFs / files).
                const splitInTranscription = m.audio.length > 0 || m.video.length > 0
                return (
                  <div className="order-2 border-b border-[var(--color-border-soft)] bg-gradient-to-b from-[var(--color-accent-bg)]/40 to-[var(--color-surface)] px-5 py-4">
                    <div className="mb-2.5 flex items-center gap-2">
                      <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-dim)]">
                        {hasAudio ? t('detail.voiceMessage') : t('detail.mediaTitle')}
                      </span>
                      <span className="text-[11.5px] text-[var(--color-text-dim)]">
                        · {t('detail.fileCount', { count: fileTotal })}
                      </span>
                      {hasAnalyzableContent && !splitInTranscription && (
                        <button
                          type="button"
                          onClick={analyzeForTicketSplit}
                          disabled={analyzingTicket}
                          title={t('detail.suggestSplit')}
                          className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium text-[var(--color-accent)] transition-colors hover:bg-[var(--color-accent-bg)] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {analyzingTicket ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                          {analyzingTicket ? t('detail.suggestSplitInProgress') : t('detail.suggestSplit')}
                        </button>
                      )}
                    </div>

                    {analyzingTicket && (
                      <div className="-mt-1 mb-2 h-1 overflow-hidden rounded-full bg-[var(--color-accent-bg)]">
                        <div className="h-full w-1/3 rounded-full bg-[var(--color-accent)] animate-progress-indeterminate" />
                      </div>
                    )}

                    {/* Audio: waveform hero + transcription block per file */}
                    {m.audio.map((url) => {
                      const owner = messages.find((mm) => mm.media_urls?.includes(url))
                      const clip = owner
                        ? ticket.audio_clips?.find((c) => c.message_id === owner.id) ?? null
                        : null
                      const tx = owner?.transcription ?? null
                      const sum = owner?.summary ?? null
                      const isTranscribing = transcribing === owner?.id
                      const expanded = expandedTx.has(url)
                      return (
                        <div key={url} className="space-y-2.5 [&:not(:first-child)]:mt-3">
                          <WaveformPlayer
                            url={url}
                            clip={clip}
                            playbackRate={audioSpeed}
                            onPlaybackRateChange={onAudioSpeedChange}
                          />
                          <TranscriptionSpoiler
                            title={t('detail.transcription')}
                            tx={tx}
                            sum={sum}
                            expanded={expanded}
                            isTranscribing={isTranscribing}
                            onToggle={() => toggleTx(url)}
                            onRegenerate={owner ? () => transcribeMessage(owner) : null}
                            onSplit={hasAnalyzableContent ? analyzeForTicketSplit : null}
                            analyzing={analyzingTicket}
                            onRewrite={
                              sum && tx
                                ? () => onUpdate(ticket.id, { title: sum, description: tx })
                                : null
                            }
                            onAddToDesc={tx ? () => addTextToDesc(tx) : null}
                            t={t}
                          />
                        </div>
                      )
                    })}

                    {/* Video transcription block — one per attached video.
                        Same Whisper + Claude path as the voice notes (the
                        edge function now accepts mp4/m4v/mov/webm/3gp). */}
                    {m.video.map((url) => {
                      const owner = messages.find((mm) => mm.media_urls?.includes(url))
                      if (!owner) return null
                      const tx = owner.transcription ?? null
                      const sum = owner.summary ?? null
                      const isTranscribingVideo = transcribing === owner.id
                      const expanded = expandedTx.has(url)
                      return (
                        <div key={`vt-${url}`} className="mt-3">
                          <TranscriptionSpoiler
                            title={t('detail.videoTranscription')}
                            tx={tx}
                            sum={sum}
                            expanded={expanded}
                            isTranscribing={isTranscribingVideo}
                            onToggle={() => toggleTx(url)}
                            onRegenerate={() => transcribeMessage(owner)}
                            onSplit={hasAnalyzableContent ? analyzeForTicketSplit : null}
                            analyzing={analyzingTicket}
                            onRewrite={
                              sum && tx
                                ? () => onUpdate(ticket.id, { title: sum, description: tx })
                                : null
                            }
                            onAddToDesc={tx ? () => addTextToDesc(tx) : null}
                            t={t}
                          />
                        </div>
                      )
                    })}

                    {/* Images + videos grid */}
                    {(m.image.length > 0 || m.video.length > 0) && (
                      <div
                        className={cn(
                          'grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-1.5',
                          hasAudio && 'mt-3',
                        )}
                      >
                        {m.image.map((url) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => setZoom(url)}
                            title={t('card.viewImages')}
                            className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] transition-colors hover:border-[var(--color-link)]"
                          >
                            <img src={url} alt="" loading="lazy" className="h-full w-full object-contain" />
                          </button>
                        ))}
                        {m.video.map((url) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => setZoom(url)}
                            title={t('card.viewVideos')}
                            className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--color-border-soft)] bg-black/80 transition-colors hover:border-[var(--color-link)]"
                          >
                            <video src={url} preload="metadata" muted className="h-full w-full object-contain" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/35 transition-colors group-hover:bg-black/45">
                              <Play className="h-7 w-7 text-white drop-shadow-md" fill="currentColor" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* PDFs + files */}
                    {(m.pdf.length > 0 || m.file.length > 0) && (
                      <div className="mt-2 space-y-1">
                        {m.pdf.map((url) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => setZoom(url)}
                            className="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                          >
                            <FileText className="h-4 w-4 shrink-0 text-[var(--color-link)]" />
                            <span className="truncate">{filenameFromUrl(url)}</span>
                          </button>
                        ))}
                        {m.file.map((url) =>
                          isDocx(url) ? (
                            // .docx → lightweight in-browser preview (mammoth,
                            // lazy-loaded). Click previews; the modal has a
                            // download too.
                            <button
                              key={url}
                              type="button"
                              onClick={() => setDocxPreviewUrl(url)}
                              title={t('docx.previewTooltip')}
                              className="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                            >
                              <FileText className="h-4 w-4 shrink-0 text-[var(--color-link)]" />
                              <span className="truncate">{filenameFromUrl(url)}</span>
                              <span className="ml-auto shrink-0 rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                                docx
                              </span>
                            </button>
                          ) : (
                            <a
                              key={url}
                              href={url}
                              download
                              className="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3 py-1.5 text-left text-[12px] text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                            >
                              <Paperclip className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                              <span className="truncate">{filenameFromUrl(url)}</span>
                            </a>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Ticket-level split proposal */}
              {(ticket.proposed_splits?.length ?? 0) >= 2 && (
                <div className="order-3 px-5 py-4">
                  <div className="rounded-2xl border border-[var(--color-link)]/30 bg-[var(--color-progress-bg)] p-4">
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-link)]">
                      <Scissors className="h-3.5 w-3.5" />
                      {t('detail.splitProposedHeader', { count: ticket.proposed_splits!.length })}
                    </div>
                    <ol className="mb-3 space-y-2 pl-5 text-[13px] text-[var(--color-text-strong)]">
                      {ticket.proposed_splits!.map((s, i) => {
                        const hasClips = (s.audio_clips?.length ?? 0) > 0
                        return (
                          <li key={i} className="list-decimal leading-snug">
                            <div className="font-semibold">{s.title}</div>
                            {s.description && (
                              <div className="mt-0.5 text-[12px] text-[var(--color-text)]">{s.description}</div>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-[var(--color-text-dim)]">
                              <span>{t('detail.sourceMsgCount', { count: s.source_message_ids?.length ?? 0 })}</span>
                              {hasClips && (
                                <span className="inline-flex items-center gap-1 rounded-md bg-[var(--color-surface)] px-1.5 py-0.5 text-[var(--color-link)]">
                                  <Scissors className="h-2.5 w-2.5" />
                                  {t('detail.audioBounded')}
                                </span>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ol>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => applyTicketSplit(ticket.proposed_splits!)}
                        disabled={applyingSplit}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-60"
                      >
                        {applyingSplit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        {t('detail.splitInto', { count: ticket.proposed_splits!.length })}
                      </button>
                      <button
                        type="button"
                        onClick={dismissTicketSplit}
                        disabled={applyingSplit}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--color-text-dim)] transition-all hover:text-[var(--color-text)] disabled:opacity-60"
                      >
                        {t('detail.dismiss')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* DESCRIPTION — leads the Contenu tab (order-1). */}
              <section className="order-1 px-5 py-3.5">
                <SectionHeader icon={<AlignLeft className="h-4 w-4" />}>
                  {t('detail.descriptionTitle')}
                </SectionHeader>
                {editingDesc ? (
                  <div className="space-y-2">
                    <RichEditor
                      innerRef={descRef}
                      value={descDraft}
                      onChange={setDescDraft}
                      placeholder={t('detail.descriptionPlaceholder', '…')}
                      className="block min-h-[110px] w-full rounded-xl border border-[var(--color-link)] bg-[var(--color-surface)] px-3 py-2 text-[13px] leading-relaxed text-[var(--color-text)]"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveDesc}
                        className="rounded-lg bg-[var(--color-accent)] px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors hover:bg-[var(--color-accent-hover)]"
                      >
                        {t('detail.descriptionSave')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDescDraft(ticket.description ?? '')
                          setEditingDesc(false)
                        }}
                        className="rounded-lg px-3 py-1.5 text-[12px] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)]"
                      >
                        {t('detail.descriptionCancel')}
                      </button>
                    </div>
                  </div>
                ) : ticket.description ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditingDesc(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setEditingDesc(true)
                      }
                    }}
                    className="group relative block w-full cursor-pointer rounded-xl p-3 -m-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    <div
                      className="rich-text pr-8 text-[13.5px] leading-relaxed text-[var(--color-text)]"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(ticket.description),
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        const text = htmlToText(ticket.description ?? '')
                        const copy = async () => {
                          try {
                            if (navigator.clipboard?.writeText) {
                              await navigator.clipboard.writeText(text)
                            } else {
                              const ta = document.createElement('textarea')
                              ta.value = text
                              ta.style.position = 'fixed'
                              ta.style.opacity = '0'
                              document.body.appendChild(ta)
                              ta.select()
                              document.execCommand('copy')
                              document.body.removeChild(ta)
                            }
                            setDescCopied(true)
                            window.setTimeout(() => setDescCopied(false), 1500)
                          } catch (err) {
                            console.warn('description copy failed', err)
                          }
                        }
                        void copy()
                      }}
                      aria-label={t('detail.descriptionCopy')}
                      title={descCopied ? t('detail.descriptionCopied') : t('detail.descriptionCopy')}
                      className="absolute right-2 top-2 rounded-lg p-1.5 text-[var(--color-text-dim)] opacity-0 transition-all hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)] group-hover:opacity-100 focus:opacity-100"
                    >
                      {descCopied ? (
                        <Check className="h-3.5 w-3.5 text-[var(--color-success)]" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingDesc(true)}
                    className="block w-full rounded-xl border border-dashed border-[var(--color-border)] px-3 py-2.5 text-left text-[12.5px] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    {t('detail.descriptionPlaceholder')}
                  </button>
                )}
              </section>

              {/* DROP ZONE — images / videos / PDFs / files dropped here are
                  uploaded and appended to ticket.media_urls (so they land in
                  the hero media section of this tab). */}
              <section className="order-4 border-t border-[var(--color-border-soft)] px-5 py-3.5">
                <SectionHeader icon={<Paperclip className="h-4 w-4" />}>
                  {t('detail.attachmentsTitle')}
                </SectionHeader>
                <input
                  ref={cardFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => void onCardFilesPicked(e.target.files)}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => cardFileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      cardFileInputRef.current?.click()
                    }
                  }}
                  onDragEnter={onCardDragEnter}
                  onDragOver={onCardDragOver}
                  onDragLeave={onCardDragLeave}
                  onDrop={onCardDrop}
                  className={cn(
                    'flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-3 text-[12.5px] transition-colors',
                    isDraggingCardFiles
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)]/80 font-semibold text-[var(--color-accent-text)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface-2)]/40 text-[var(--color-text-dim)] hover:border-[var(--color-link)] hover:text-[var(--color-link)]',
                  )}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {isDraggingCardFiles
                    ? t('reply.dropFilesHere')
                    : t('detail.attachmentsDropHint')}
                </div>

                {cardUploads.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1.5">
                    {cardUploads.map((u) => (
                      <div
                        key={u.id}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[12px]',
                          u.error
                            ? 'border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)]'
                            : 'border-[var(--color-border-soft)] bg-[var(--color-surface)]',
                        )}
                      >
                        {u.uploading ? (
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--color-text-dim)]" />
                        ) : (
                          <X className="h-4 w-4 shrink-0 text-[var(--color-danger)]" />
                        )}
                        <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                          {u.filename}
                        </span>
                        {u.error && (
                          <button
                            type="button"
                            onClick={() => removeCardUpload(u.id)}
                            title={t('common.close')}
                            aria-label={t('common.close')}
                            className="rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

                </div>
              )}

              {/* ── MESSAGERIE ─────────────────────────────────────── */}
                {activeTab === 'messagerie' && (
                  <div className="border-t border-[var(--color-border-soft)]">
                    {/* Selection toolbar — pick messages and extract them into
                        a brand-new card (the messages stay in this card too). */}
                    {messages.length > 0 && (
                      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border-soft)] px-5 py-2">
                        {selectMode ? (
                          <>
                            <span className="text-[12px] text-[var(--color-text-dim)]">
                              {t('detail.split.selectedCount', { count: selectedMsgIds.size })}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={cancelMessageSplit}
                                className="rounded-lg px-2.5 py-1 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                              >
                                {t('common.cancel')}
                              </button>
                              <button
                                type="button"
                                onClick={confirmMessageSplit}
                                disabled={selectedMsgIds.size === 0 || splittingMessages}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-2.5 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {splittingMessages ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Scissors className="h-3.5 w-3.5" />
                                )}
                                {t('detail.split.createCard')}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="ml-auto flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setSelectMode(true)}
                              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                              title={t('detail.split.extractHint')}
                            >
                              <Scissors className="h-3.5 w-3.5" />
                              {t('detail.split.extract')}
                            </button>
                            {/* Mark as replied — clears the awaiting-reply
                                state (stops the card's green bouncing bubble)
                                WITHOUT sending a message, for when the matter
                                was already handled off-app (phone, in person…). */}
                            {ticket.awaiting_reply && (
                              <button
                                type="button"
                                onClick={() =>
                                  onUpdate(ticket.id, { awaiting_reply: false })
                                }
                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-medium text-emerald-600 transition-colors hover:bg-emerald-500/10"
                                title={t('detail.markReplied.hint')}
                              >
                                <MessagesSquare className="h-3.5 w-3.5 fill-emerald-500/90" />
                                {t('detail.markReplied.label')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Conversation thread (chat bubbles) */}
                    <div className="space-y-2.5 px-5 py-4">
                      {loading ? (
                        <div className="text-[12px] text-[var(--color-text-dim)]">
                          {t('common.loading')}
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-6 text-center text-[12.5px] text-[var(--color-text-dim)]">
                          {t('detail.noSourceMessages')}
                        </div>
                      ) : (
                        messages.map((m) => {
                          const isOut = m.direction === 'outbound'
                          const authorName = isOut
                            ? m.author_id === currentUserId
                              ? t('reply.you')
                              : profileName(profilesById[m.author_id ?? '']) || ''
                            : null
                          const quoted = m.reply_to_message_id
                            ? messages.find((x) => x.id === m.reply_to_message_id)
                            : null
                          const media = groupMedia(m.media_urls)
                          const quotedText =
                            quoted?.body?.trim() ||
                            quoted?.transcription?.trim() ||
                            t('reply.mediaGeneric')
                          const selected = selectedMsgIds.has(m.id)
                          return (
                            <div
                              key={m.id}
                              onClick={selectMode ? () => toggleMsgSelected(m.id) : undefined}
                              className={cn(
                                'group flex items-start gap-2',
                                isOut ? 'flex-row-reverse' : 'flex-row',
                                selectMode && 'cursor-pointer',
                              )}
                            >
                              {selectMode && (
                                <span
                                  className={cn(
                                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                                    selected
                                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
                                      : 'border-[var(--color-border)] bg-[var(--color-surface)]',
                                  )}
                                >
                                  {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                                </span>
                              )}
                              <div
                                className={cn(
                                  'flex min-w-0 max-w-[80%] flex-col gap-1',
                                  isOut ? 'items-end' : 'items-start',
                                )}
                              >
                                <div
                                  className={cn(
                                    // max-w-full + w-fit so the bubble shrinks
                                    // to its text wrap inside the 80%-capped
                                    // column wrapper. Without it, long single-
                                    // line text (a freshly typed reply) blows
                                    // past the wrapper and overflows the row.
                                    'w-fit max-w-full rounded-2xl px-3 py-2 text-[13px] leading-relaxed',
                                    isOut
                                      ? 'rounded-br-md bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                                      : 'rounded-bl-md bg-[var(--color-surface-2)] text-[var(--color-text)]',
                                    // In select mode the bubble is inert (clicks
                                    // toggle selection instead of zooming media).
                                    selectMode && 'pointer-events-none',
                                    selected &&
                                      'ring-2 ring-[var(--color-accent)] ring-offset-1 ring-offset-[var(--color-surface)]',
                                  )}
                                >
                                {/* Quoted reply preview */}
                                {quoted && (
                                  <div
                                    className={cn(
                                      'mb-1.5 truncate border-l-2 pl-2 text-[11.5px] italic',
                                      isOut
                                        ? 'border-[var(--color-accent)]/40 text-[var(--color-accent-text)]/80'
                                        : 'border-[var(--color-accent)] text-[var(--color-text-dim)]',
                                    )}
                                  >
                                    {quotedText}
                                  </div>
                                )}

                                {/* Text body */}
                                {m.body?.trim() && (
                                  <div className="whitespace-pre-wrap break-words">
                                    {m.body}
                                  </div>
                                )}

                                {/* Audio + its transcription (product plus:
                                    the contact's voice note shown as text). */}
                                {media.audio.length > 0 && (
                                  <div className="mt-1">
                                    <InlineAudio
                                      urls={media.audio}
                                      playbackRate={audioSpeed}
                                      onPlaybackRateChange={onAudioSpeedChange}
                                    />
                                    {m.transcription?.trim() && (
                                      <div
                                        className={cn(
                                          'mt-1.5 whitespace-pre-wrap break-words text-[12.5px] italic',
                                          isOut
                                            ? 'text-[var(--color-accent-text)]/80'
                                            : 'text-[var(--color-text-dim)]',
                                        )}
                                      >
                                        {m.transcription}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Images / videos */}
                                {(media.image.length > 0 || media.video.length > 0) && (
                                  <div className="mt-1 grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-1">
                                    {media.image.map((url) => (
                                      <button
                                        key={url}
                                        type="button"
                                        onClick={() => setZoom(url)}
                                        className="aspect-square overflow-hidden rounded-lg bg-black/10"
                                      >
                                        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                                      </button>
                                    ))}
                                    {media.video.map((url) => (
                                      <button
                                        key={url}
                                        type="button"
                                        onClick={() => setZoom(url)}
                                        className="relative aspect-square overflow-hidden rounded-lg bg-black/70"
                                      >
                                        <video src={url} preload="metadata" muted className="h-full w-full object-contain" />
                                        <Play className="absolute inset-0 m-auto h-6 w-6 text-white" fill="currentColor" />
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {/* PDFs / files */}
                                {(media.pdf.length > 0 || media.file.length > 0) &&
                                  [...media.pdf, ...media.file].map((url) => (
                                    <a
                                      key={url}
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className={cn(
                                        'mt-1 flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px]',
                                        isOut ? 'bg-white/15' : 'bg-[var(--color-surface)]',
                                      )}
                                    >
                                      <FileText className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">{filenameFromUrl(url)}</span>
                                    </a>
                                  ))}

                                {/* Fallback when nothing else to show */}
                                {!m.body?.trim() &&
                                  media.audio.length === 0 &&
                                  media.image.length === 0 &&
                                  media.video.length === 0 &&
                                  media.pdf.length === 0 &&
                                  media.file.length === 0 && (
                                    <div className="italic opacity-70">
                                      {t('reply.mediaGeneric')}
                                    </div>
                                  )}

                                {/* Meta line */}
                                <div
                                  className={cn(
                                    'mt-1 text-[10px]',
                                    isOut ? 'text-[var(--color-accent-text)]/70' : 'text-[var(--color-text-dim)]',
                                  )}
                                >
                                  {isOut && authorName
                                    ? `${t('reply.sentBy', { name: authorName })} · ${formatRelative(m.received_at)}`
                                    : formatRelative(m.received_at)}
                                </div>
                                </div>

                                {/* Reactions row, BELOW the bubble. Active
                                    reactions stay visible (small chips);
                                    inactive ones reveal on row-hover so the
                                    row truly collapses to zero buttons when
                                    nothing is set. Hidden in select mode. */}
                                {!selectMode && (
                                  <div className="flex max-w-full flex-wrap items-center gap-0.5">
                                    {MESSAGE_REACTIONS.map((r) => {
                                      const on = (m.tags ?? []).includes(r.key)
                                      return (
                                        <button
                                          key={r.key}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            toggleMessageTag(m.id, r.key)
                                          }}
                                          title={t(r.tipKey)}
                                          aria-label={t(r.tipKey)}
                                          className={cn(
                                            'h-6 w-6 items-center justify-center rounded-full text-[13px] leading-none transition-colors',
                                            on
                                              ? 'inline-flex bg-[var(--color-accent-bg)] ring-1 ring-[var(--color-accent)]'
                                              : 'hidden bg-transparent hover:bg-[var(--color-surface-2)] group-hover:inline-flex',
                                          )}
                                        >
                                          <span aria-hidden>{r.emoji}</span>
                                        </button>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>

                    {/* Composer — always present on the Messagerie tab. The
                        whole container is a drop zone: files dragged onto it
                        are staged like a click on the paperclip, and the text
                        currently in the composer becomes their caption when
                        the user hits send. */}
                    <div
                      className="relative border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 px-5 py-3"
                      onDragEnter={onComposerDragEnter}
                      onDragOver={onComposerDragOver}
                      onDragLeave={onComposerDragLeave}
                      onDrop={onComposerDrop}
                    >
                      {isDraggingFiles && (
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-[var(--color-accent)] bg-[var(--color-accent-bg)]/80 text-[12.5px] font-semibold text-[var(--color-accent-text)]">
                          {t('reply.dropFilesHere')}
                        </div>
                      )}
                      {!project ? null : !canReply ? (
                        <div className="flex items-center gap-1.5 text-[11.5px] text-[var(--color-text-dim)]">
                          <Lock className="h-3.5 w-3.5" />
                          {t('reply.noPermission')}
                        </div>
                      ) : windowExpired ? (
                        <div className="flex items-center gap-1.5 text-[11.5px] text-[var(--color-danger)]">
                          <Clock className="h-3.5 w-3.5" />
                          {t('reply.windowExpired')}
                        </div>
                      ) : (
                        <>
                          {/* Suggestions toolbar : register + on/off, tunable
                              live (owner only). Lets the user switch tone
                              when an unknown contact opens with « tu », and
                              regenerate the 3 suggestions on the fly. */}
                          {isProjectOwner && onPatchProject && (
                            <div className="relative mb-2">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-text-dim)]">
                                    <Sparkles className="h-3.5 w-3.5" />
                                    {t('reply.suggestionsLabel')}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={toggleSuggestions}
                                    role="switch"
                                    aria-checked={suggestionsEnabled}
                                    aria-label={t('reply.suggestionsLabel')}
                                    className={cn(
                                      'relative h-4 w-7 shrink-0 self-center rounded-full transition-colors',
                                      suggestionsEnabled
                                        ? 'bg-[var(--color-accent)]'
                                        : 'bg-[var(--color-border)]',
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        'absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-white shadow-sm transition-all',
                                        suggestionsEnabled ? 'left-[14px]' : 'left-0.5',
                                      )}
                                    />
                                  </button>
                                </div>
                                {suggestionsEnabled && (
                                  <button
                                    type="button"
                                    onClick={() => setTonePopoverOpen((v) => !v)}
                                    className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-2 py-0.5 text-[11px] text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                                    title={t('projectSettings.toneTitle')}
                                  >
                                    <SlidersHorizontal className="h-3 w-3 shrink-0" />
                                    <span className="truncate">
                                      {t(`projectSettings.tone.${currentTone}`)}
                                    </span>
                                  </button>
                                )}
                              </div>

                              {/* Tone popover */}
                              {tonePopoverOpen && suggestionsEnabled && (
                                <>
                                  <button
                                    type="button"
                                    aria-hidden
                                    tabIndex={-1}
                                    onClick={() => setTonePopoverOpen(false)}
                                    className="fixed inset-0 z-10 cursor-default"
                                  />
                                  <div className="absolute right-0 top-full z-20 mt-1.5 w-72 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-[var(--shadow-modal)]">
                                    <div className="mb-2 text-[11.5px] text-[var(--color-text-dim)]">
                                      {t('projectSettings.toneHint')}
                                    </div>
                                    <ToneSlider
                                      value={currentTone}
                                      disabled={false}
                                      onChange={changeTone}
                                      t={t}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {/* AI suggestions */}
                          {suggestionsEnabled &&
                            (replySuggLoading ? (
                              <div className="mb-2 flex items-center gap-2 text-[11.5px] text-[var(--color-text-dim)]">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                {t('reply.generating')}
                              </div>
                            ) : (
                              replySuggestions.length > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                  {replySuggestions.map((s, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => {
                                        setReplyDraft(s)
                                        replyComposerRef.current?.focus()
                                      }}
                                      className="max-w-full truncate rounded-full border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-2.5 py-1 text-[11.5px] text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                                      title={s}
                                    >
                                      {s}
                                    </button>
                                  ))}
                                </div>
                              )
                            ))}

                          {/* Pending attachments (files / recorded audio) */}
                          {pendingAttachments.length > 0 && (
                            <div className="mb-2 flex flex-col gap-1.5">
                              {pendingAttachments.map((a) => (
                                <div
                                  key={a.id}
                                  className={cn(
                                    'flex flex-col gap-1.5 rounded-lg border px-2 py-1.5 text-[12px]',
                                    a.error
                                      ? 'border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)]'
                                      : 'border-[var(--color-border-soft)] bg-[var(--color-surface)]',
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    {a.uploading ? (
                                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--color-text-dim)]" />
                                    ) : a.kind === 'image' && a.url ? (
                                      <img
                                        src={a.url}
                                        alt=""
                                        className="h-8 w-8 shrink-0 rounded object-cover"
                                      />
                                    ) : a.kind === 'audio' ? (
                                      <Mic className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                                    ) : a.kind === 'video' ? (
                                      <Play className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                                    ) : (
                                      <FileText className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                                    )}
                                    <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                                      {a.recorded && a.kind === 'audio'
                                        ? t('reply.voiceNote')
                                        : a.filename}
                                    </span>
                                    {/* Optional Whisper transcription for audio */}
                                    {a.kind === 'audio' && !a.error && (
                                      <button
                                        type="button"
                                        onClick={() => toggleAttachmentTranscribe(a.id)}
                                        title={t('reply.transcribeHint')}
                                        className={cn(
                                          'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium transition-colors',
                                          a.transcribe
                                            ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                                            : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-link)]',
                                        )}
                                      >
                                        <Captions className="h-3 w-3" />
                                        {t('reply.transcribe')}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeAttachment(a.id)}
                                      aria-label={t('common.delete')}
                                      className="shrink-0 rounded-full p-0.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>

                                  {/* Audio preview — re-listen before sending */}
                                  {a.kind === 'audio' && a.url && !a.uploading && (
                                    <InlineAudio
                                      urls={[a.url]}
                                      playbackRate={audioSpeed}
                                      onPlaybackRateChange={onAudioSpeedChange}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Live recording bar */}
                          {recording && (
                            <div className="mb-2 flex items-center gap-2 rounded-lg border border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px]">
                              <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-danger)]" />
                              <span className="flex-1 text-[var(--color-text)]">
                                {t('reply.recording')} ·{' '}
                                {String(Math.floor(recordSecs / 60)).padStart(2, '0')}
                                :{String(recordSecs % 60).padStart(2, '0')}
                              </span>
                              <button
                                type="button"
                                onClick={stopRecording}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-danger)] px-2.5 py-1 text-[11.5px] font-semibold text-white"
                              >
                                <Square className="h-3 w-3" fill="currentColor" />
                                {t('reply.stopRecording')}
                              </button>
                            </div>
                          )}

                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => onFilesPicked(e.target.files)}
                          />
                          <div className="flex items-end gap-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={recording}
                              title={t('reply.attachFile')}
                              aria-label={t('reply.attachFile')}
                              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Paperclip className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={recording ? stopRecording : startRecording}
                              title={t(recording ? 'reply.stopRecording' : 'reply.recordAudio')}
                              aria-label={t(recording ? 'reply.stopRecording' : 'reply.recordAudio')}
                              className={cn(
                                'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors',
                                recording
                                  ? 'border-[var(--color-danger)] bg-[var(--color-danger)] text-white'
                                  : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-dim)] hover:border-[var(--color-link)] hover:text-[var(--color-link)]',
                              )}
                            >
                              {recording ? (
                                <Square className="h-4 w-4" fill="currentColor" />
                              ) : (
                                <Mic className="h-4 w-4" />
                              )}
                            </button>
                            {/* Emoji picker — inserts at the caret position. */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setEmojiPickerOpen((v) => !v)}
                                disabled={recording}
                                title={t('reply.insertEmoji')}
                                aria-label={t('reply.insertEmoji')}
                                className={cn(
                                  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                  emojiPickerOpen
                                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-dim)] hover:border-[var(--color-link)] hover:text-[var(--color-link)]',
                                )}
                              >
                                <Smile className="h-4 w-4" />
                              </button>
                              {emojiPickerOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-[55]"
                                    onClick={() => setEmojiPickerOpen(false)}
                                  />
                                  <div className="absolute bottom-full left-0 z-[56] mb-1 flex flex-wrap items-center gap-1 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-1.5 shadow-[var(--shadow-modal)]">
                                    {MESSAGE_REACTIONS.map((r) => (
                                      <button
                                        key={r.key}
                                        type="button"
                                        onClick={() => {
                                          insertEmojiInReply(r.emoji)
                                          setEmojiPickerOpen(false)
                                        }}
                                        title={t(r.tipKey)}
                                        aria-label={t(r.tipKey)}
                                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[16px] leading-none transition-colors hover:bg-[var(--color-surface-2)]"
                                      >
                                        <span aria-hidden>{r.emoji}</span>
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            <textarea
                              ref={replyComposerRef}
                              value={replyDraft}
                              onChange={(e) => setReplyDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                  e.preventDefault()
                                  void sendReply()
                                }
                              }}
                              rows={1}
                              maxLength={4096}
                              spellCheck
                              autoCorrect="on"
                              placeholder={t('reply.composerPlaceholder')}
                              className="block max-h-32 min-h-[40px] flex-1 resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] leading-relaxed text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                            />
                            {/* AI spell/grammar fix (complements native check) */}
                            <button
                              type="button"
                              onClick={correctDraft}
                              disabled={
                                correcting ||
                                sendingReply ||
                                replyDraft.trim().length === 0
                              }
                              title={t('reply.correctTooltip')}
                              aria-label={t('reply.correctTooltip')}
                              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {correcting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <SpellCheck className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={sendReply}
                              disabled={
                                sendingReply ||
                                recording ||
                                (replyDraft.trim().length === 0 &&
                                  pendingAttachments.length === 0)
                              }
                              className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {sendingReply ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Send className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                          <div className="mt-1 text-[10.5px] text-[var(--color-text-dim)]">
                            {t('detail.comments.composerHint')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ── COMMENTAIRES ───────────────────────────────────── */}
                {activeTab === 'commentaires' && (
                  <div className="border-t border-[var(--color-border-soft)] px-5 py-4">
                    <div className="flex gap-2">
                      <Avatar
                        profile={profilesById[currentUserId]}
                        userId={currentUserId}
                        className="mt-0.5 h-7 w-7"
                        textClassName="text-[10px]"
                      />
                      <div className="min-w-0 flex-1 space-y-2">
                        <RichEditor
                          value={commentDraft}
                          onChange={setCommentDraft}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault()
                              void postComment()
                            }
                          }}
                          placeholder={t('detail.comments.composerPlaceholder')}
                          className="block min-h-[60px] w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] leading-relaxed text-[var(--color-text)] focus:border-[var(--color-link)]"
                        />

                        {/* Dedicated drop zone — clearly separated from the
                            rich editor so contentEditable can't capture the
                            drop. Clickable to open the file picker too. */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => commentFileInputRef.current?.click()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              commentFileInputRef.current?.click()
                            }
                          }}
                          onDragEnter={onCommentDragEnter}
                          onDragOver={onCommentDragOver}
                          onDragLeave={onCommentDragLeave}
                          onDrop={onCommentDrop}
                          className={cn(
                            'flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-2.5 text-[12px] transition-colors',
                            isDraggingCommentFiles
                              ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)]/80 text-[var(--color-accent-text)] font-semibold'
                              : 'border-[var(--color-border)] bg-[var(--color-surface-2)]/40 text-[var(--color-text-dim)] hover:border-[var(--color-link)] hover:text-[var(--color-link)]',
                          )}
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                          {isDraggingCommentFiles
                            ? t('reply.dropFilesHere')
                            : t('detail.comments.dropZoneHint')}
                        </div>

                        {/* Pending attachments (drag-drop / paperclip) */}
                        {commentAttachments.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            {commentAttachments.map((a) => (
                              <div
                                key={a.id}
                                className={cn(
                                  'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[12px]',
                                  a.error
                                    ? 'border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)]'
                                    : 'border-[var(--color-border-soft)] bg-[var(--color-surface)]',
                                )}
                              >
                                {a.uploading ? (
                                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--color-text-dim)]" />
                                ) : a.error ? (
                                  <X className="h-4 w-4 shrink-0 text-[var(--color-danger)]" />
                                ) : a.kind === 'image' ? (
                                  <Paperclip className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                                ) : (
                                  <FileText className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                                )}
                                <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                                  {a.filename}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeCommentAttachment(a.id)}
                                  title={t('common.close')}
                                  aria-label={t('common.close')}
                                  className="rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <input
                          ref={commentFileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => onCommentFilesPicked(e.target.files)}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => commentFileInputRef.current?.click()}
                            title={t('reply.attachFile')}
                            aria-label={t('reply.attachFile')}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)]"
                          >
                            <Paperclip className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={postComment}
                            disabled={
                              postingComment ||
                              (isRichEmpty(commentDraft) &&
                                commentAttachments.filter(
                                  (a) => a.url && !a.error && !a.uploading,
                                ).length === 0)
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {postingComment ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5" />
                            )}
                            {t('detail.comments.post')}
                          </button>
                          <span className="text-[10.5px] text-[var(--color-text-dim)]">
                            {autoPostPending
                              ? t('detail.comments.autoPostPending')
                              : t('detail.comments.composerHint')}
                          </span>
                        </div>
                      </div>
                    </div>
                    {comments.length > 0 && (
                      <ul className="mt-4 space-y-3">
                        {comments.map((c) => {
                          const author = profilesById[c.author_id]
                          const isMine = c.author_id === currentUserId
                          const isEditing = editingCommentId === c.id
                          const isDeleted = !!c.deleted_at
                          const edited = !isDeleted && c.updated_at !== c.created_at
                          return (
                            <li key={c.id} className="flex gap-2">
                              <Avatar
                                profile={author}
                                userId={c.author_id}
                                className="mt-0.5 h-7 w-7"
                                textClassName="text-[10px]"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[11.5px] text-[var(--color-text-dim)]">
                                  <span className="font-semibold text-[var(--color-text)]">
                                    {profileName(author) || t('detail.comments.unknownAuthor')}
                                  </span>
                                  <span>·</span>
                                  <span title={formatAiDate(c.created_at)}>{formatRelative(c.created_at)}</span>
                                  {edited && (
                                    <>
                                      <span>·</span>
                                      <span className="italic">{t('detail.comments.edited')}</span>
                                    </>
                                  )}
                                </div>
                                {isDeleted ? (
                                  <div className="mt-0.5 italic text-[12.5px] text-[var(--color-text-dim)]">
                                    {t('detail.comments.deletedPlaceholder')}
                                  </div>
                                ) : isEditing ? (
                                  <div className="mt-1 space-y-2">
                                    <RichEditor
                                      value={editingCommentDraft}
                                      onChange={setEditingCommentDraft}
                                      className="block min-h-[60px] w-full rounded-xl border border-[var(--color-link)] bg-[var(--color-surface)] px-3 py-2 text-[13px] leading-relaxed text-[var(--color-text)]"
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => saveEditComment(c.id)}
                                        disabled={savingCommentEdit || isRichEmpty(editingCommentDraft)}
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-60"
                                      >
                                        {savingCommentEdit && <Loader2 className="h-3 w-3 animate-spin" />}
                                        {t('common.save')}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={cancelEditComment}
                                        className="rounded-lg px-3 py-1 text-[12px] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)]"
                                      >
                                        {t('common.cancel')}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="group relative">
                                    {c.body && (
                                      <div
                                        className="rich-text mt-0.5 break-words pr-14 text-[13.5px] leading-relaxed text-[var(--color-text)]"
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeRichHtml(c.body),
                                        }}
                                      />
                                    )}
                                    {/* Attachments (images / videos / PDFs /
                                        files) dropped into the comment. */}
                                    {c.media_urls && c.media_urls.length > 0 && (
                                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {(() => {
                                          const grouped = groupMedia(c.media_urls)
                                          return (
                                            <>
                                              {grouped.image.map((url) => (
                                                <button
                                                  key={url}
                                                  type="button"
                                                  onClick={() => setZoom(url)}
                                                  className="aspect-square h-20 overflow-hidden rounded-lg bg-black/10"
                                                >
                                                  <img
                                                    src={url}
                                                    alt=""
                                                    loading="lazy"
                                                    className="h-full w-full object-cover"
                                                  />
                                                </button>
                                              ))}
                                              {grouped.video.map((url) => (
                                                <button
                                                  key={url}
                                                  type="button"
                                                  onClick={() => setZoom(url)}
                                                  className="relative aspect-square h-20 overflow-hidden rounded-lg bg-black/70"
                                                >
                                                  <video
                                                    src={url}
                                                    preload="metadata"
                                                    muted
                                                    className="h-full w-full object-contain"
                                                  />
                                                  <Play
                                                    className="absolute inset-0 m-auto h-6 w-6 text-white"
                                                    fill="currentColor"
                                                  />
                                                </button>
                                              ))}
                                              {[...grouped.pdf, ...grouped.file].map(
                                                (url) => (
                                                  <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-surface-2)] px-2 py-1 text-[12px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-3)]"
                                                  >
                                                    <FileText className="h-3.5 w-3.5 shrink-0" />
                                                    <span className="truncate">
                                                      {filenameFromUrl(url)}
                                                    </span>
                                                  </a>
                                                ),
                                              )}
                                            </>
                                          )
                                        })()}
                                      </div>
                                    )}
                                    {isMine && (
                                      <div className="absolute right-0 top-0 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                                        <button
                                          type="button"
                                          onClick={() => startEditComment(c)}
                                          aria-label={t('common.edit')}
                                          title={t('common.edit')}
                                          className="rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteComment(c)}
                                          aria-label={t('common.delete')}
                                          title={t('common.delete')}
                                          className="rounded-md p-1 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}
            </div>

          </div>

          {/* ── FOOTER — creation / update meta (moved out of the old rail)
                + delete action ─────────────────────────────────────────── */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--color-border-soft)] px-5 py-2.5 text-[11px] text-[var(--color-text-dim)]">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span
                className="cursor-default"
                title={`${t('detail.created')} · ${formatDateTime(ticket.created_at)}`}
              >
                {t('detail.created')} · {formatRelative(ticket.created_at)}
              </span>
              <span>·</span>
              <span
                className="cursor-default"
                title={`${t('detail.updatedTooltip')} · ${formatDateTime(ticket.updated_at)}`}
              >
                {t('detail.lastUpdated', { relative: formatRelative(ticket.updated_at) })}
              </span>
              {ticket.ai_confidence != null && (
                <>
                  <span>·</span>
                  <span className="font-medium text-[var(--color-success)]">
                    {t('detail.aiConfidence', { value: Math.round(ticket.ai_confidence * 100) })}
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={deleteTicket}
              title={t('detail.deleteTooltip')}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 font-medium text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger-bg)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('common.delete')}
            </button>
          </div>
        </div>
      </div>

      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/85 p-6"
          onClick={() => setZoom(null)}
        >
          {mediaKind(zoom) === 'pdf' ? (
            <iframe
              src={zoom}
              title="PDF"
              className="h-[88vh] w-[92vw] max-w-5xl rounded-2xl border-0 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : mediaKind(zoom) === 'video' ? (
            <video
              src={zoom}
              controls
              autoPlay
              className="max-h-full max-w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={zoom}
              alt=""
              className="max-h-full max-w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="absolute right-6 top-6 rounded-full bg-white/90 p-2.5 text-slate-700 shadow-lg transition-colors hover:bg-white"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Lightweight .docx preview (mammoth, lazy-loaded). */}
      <DocxPreview url={docxPreviewUrl} onClose={() => setDocxPreviewUrl(null)} />
    </>
  )
}

/** Single-audio block in the Médias section : InlineAudio player + the
 *  Whisper transcription / Claude summary side. When the owning source
 *  message has neither, exposes a « Transcrire » button to generate
 *  both. Hides the action entirely for orphan audios (no message_id
 *  available to send to the transcribe-audio edge function).
 *
 *  This brings audio insights back into the visible part of the modal:
 *  the heavy « Messages source » spoiler stays collapsed, but the user
 *  still sees the summary of each vocal at a glance. */
/** Best-effort « pretty » filename for an attachment URL. We strip the
 *  query string + decode percent-escapes so a Supabase storage URL like
 *  `…/audio/1709%20note%20vocale.ogg?token=…` reads as
 *  « 1709 note vocale.ogg ». Falls back to the host if no path is left. */
function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url)
    const last = u.pathname.split('/').filter(Boolean).pop() ?? u.host
    return decodeURIComponent(last)
  } catch {
    return url
  }
}

function SectionHeader({
  icon,
  children,
  action,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  /** Optional trailing element (typically a small icon button), right-aligned. */
  action?: React.ReactNode
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2 text-[12.5px] font-semibold text-[var(--color-text-strong)]">
      <span className="shrink-0 text-[var(--color-text-dim)]">{icon}</span>
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </div>
  )
}

/** Collapsible transcription block (audio or video). Collapsed by default so
 *  the Contenu tab leads with the description; the header carries the two
 *  actions — « découpage » (Scissors) and « régénérer » (Sparkles) — side by
 *  side, plus a one-line preview of the transcription when collapsed. */
function TranscriptionSpoiler({
  title,
  tx,
  sum,
  expanded,
  isTranscribing,
  onToggle,
  onRegenerate,
  onSplit,
  analyzing,
  onRewrite,
  onAddToDesc,
  t,
}: {
  title: string
  tx: string | null
  sum: string | null
  expanded: boolean
  isTranscribing: boolean
  onToggle: () => void
  onRegenerate: (() => void) | null
  onSplit: (() => void) | null
  analyzing: boolean
  onRewrite: (() => void) | null
  onAddToDesc: (() => void) | null
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const preview = (sum || tx || '').trim()
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)]">
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-[var(--color-text-dim)] transition-transform',
              expanded ? '' : '-rotate-90',
            )}
          />
          <Captions className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-dim)]" />
          <span className="shrink-0 text-[13.5px] font-semibold text-[var(--color-text-strong)]">
            {title}
          </span>
          {!expanded && preview && (
            <span className="min-w-0 truncate text-[11.5px] font-normal text-[var(--color-text-dim)]">
              — {preview}
            </span>
          )}
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          {onSplit && (
            <button
              type="button"
              onClick={onSplit}
              disabled={analyzing}
              title={t('detail.suggestSplit')}
              aria-label={t('detail.suggestSplit')}
              className="inline-flex items-center justify-center rounded-lg p-1.5 text-[var(--color-link)] transition-colors hover:bg-[var(--color-accent-bg)] disabled:opacity-60"
            >
              {analyzing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Scissors className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={isTranscribing}
              title={
                tx
                  ? t('detail.transcriptionRegenerate')
                  : t('detail.transcriptionGenerate')
              }
              aria-label={
                tx
                  ? t('detail.transcriptionRegenerate')
                  : t('detail.transcriptionGenerate')
              }
              className="inline-flex items-center justify-center rounded-lg p-1.5 text-[var(--color-accent)] transition-colors hover:bg-[var(--color-accent-bg)] disabled:opacity-60"
            >
              {isTranscribing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[var(--color-border-soft)] px-3.5 pb-3.5 pt-3">
          {isTranscribing ? (
            <div className="space-y-1.5">
              <div className="h-2.5 w-full animate-pulse rounded bg-[var(--color-surface-2)]" />
              <div className="h-2.5 w-4/5 animate-pulse rounded bg-[var(--color-surface-2)]" />
              <div className="h-2.5 w-3/5 animate-pulse rounded bg-[var(--color-surface-2)]" />
            </div>
          ) : tx ? (
            <div className="space-y-2.5">
              {sum && (
                <div className="rounded-lg bg-[var(--color-surface-2)] px-3 py-2 text-[12.5px] italic leading-relaxed text-[var(--color-text)]">
                  {sum}
                </div>
              )}
              <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--color-text)]">
                {tx}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {onRewrite && (
                  <button
                    type="button"
                    onClick={onRewrite}
                    className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-accent-bg)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--color-accent-text)] transition-colors hover:bg-[var(--color-accent)] hover:text-white"
                    title={t('detail.rewriteTitleDescTooltip')}
                  >
                    <Sparkles className="h-3 w-3" />
                    {t('detail.rewriteWithAi')}
                  </button>
                )}
                {onAddToDesc && (
                  <button
                    type="button"
                    onClick={onAddToDesc}
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    <AlignLeft className="h-3 w-3" />
                    {t('detail.addToDescription')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-[13px] leading-relaxed text-[var(--color-text-dim)]">
              {t('detail.transcriptionEmpty')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Renders a message's media (images grid, video, audio players, PDF, files). */
